import { BusinessPrerequisites, BusinessSingpassButton } from '../../borrower/myinfo-business/BusinessEntry';
import { getPersonData } from "../../utils/myinfoData";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cbsScreenshot from "../../assets/img/CBS4.jpeg";
import singpassmobilepage from "../../assets/img/Singpasslogin-screenshot.png";
import Cookies from "js-cookie";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { message, Modal } from "antd";
import pdfToText from "react-pdftotext";
import { UserOutlined, QuestionOutlined } from "@ant-design/icons";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
  Font,
  PDFViewer,
} from "@react-pdf/renderer";
import logo from "../../assets/img/SL-01.png";
import boldFont from "../../assets/font/Poppins-SemiBold.ttf";
import cbsImage from "../smart-lend-frontend/src/img/CBSlogo.png";
import SVGICON from "../../assets/img/fssvg.svg";
import DoneIcon from "@mui/icons-material/Done";
import SlLogo from "../../assets/img/SL-01.png";
import { useHistory } from "react-router-dom";
import Tour from "reactour";
import API_BASE from "../../config/api";

Font.register({
  family: "Poppins",
  fonts: [
    { src: boldFont, fontWeight: 700 }, // Bold (700)
  ],
});

const trackCbsAnalyticsEvent = (eventType, metadata = {}) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("smartlend:analytics-event", {
      detail: {
        event_type: eventType,
        area: "dashboard",
        page_path: window.location.pathname,
        metadata: {
          campaign: "free_cbs_report",
          ...metadata,
        },
      },
    })
  );
};

const cbsIntentFlows = {
  modern: {
    questions: [
      {
        id: "reason",
        eyebrow: "⚡ Takes 10 seconds",
        title: "What are you hoping to sort out today?",
        subtitle:
          "Tell us once and we'll tailor the right options to you — quick, simple, and completely free.",
        options: [
          {
            value: "property",
            icon: "🏠",
            title: "Home / Property financing",
            description: "Buying, refinancing or unlocking equity",
          },
          {
            value: "business",
            icon: "💼",
            title: "Business / SME financing",
            description: "Working capital, expansion or cash flow",
          },
          {
            value: "personal",
            icon: "💳",
            title: "Personal financing",
            description: "Consolidate debt or fund a big goal",
          },
          {
            value: "credit_health",
            icon: "👀",
            title: "Just checking my credit health",
            description: "See where I stand, no pressure",
          },
        ],
      },
      {
        id: "second_opinion",
        eyebrow: "🔒 Private & judgment-free",
        title: "Smart move — and you're in good company.",
        subtitle: [
          {
            text: "Most people check their CBS before making a big financial decision.",
            bold: true,
          },
          {
            text: " There's nothing to hide here — it's simply the savvy first step. While you're at it, want a free second opinion on what you'd actually qualify for?",
          },
        ],
        callouts: [
          {
            icon: "🎁",
            tone: "green",
            text: [
              { text: "100% free assessment.", bold: true },
              {
                text: " No obligation, no fees, and it won't affect your credit score — ever.",
              },
            ],
          },
        ],
        proof: {
          avatars: ["R", "M", "J", "A"],
          text: [{ text: "12,000+", bold: true }, { text: " Singaporeans checked theirs this month" }],
        },
        actions: [
          {
            label: "Yes, give me a second opinion",
            value: "yes_second_opinion",
            style: "primary",
            nextScreen: 2,
          },
          {
            label: "Skip for now →",
            value: "skip_for_now",
            style: "link",
            nextScreen: 3,
          },
        ],
      },
      {
        id: "compare_options",
        eyebrow: "✨ You might be leaving money on the table",
        title: "What if there's a better deal waiting for you?",
        subtitle: [
          { text: "Instead of approaching them one by one, SmartLend compares offers from " },
          {
            text: "banks, financial institutions and licensed lenders",
            bold: true,
          },
          { text: " — all in one place. A " },
          { text: "dedicated advisor", bold: true },
          {
            text: " then personally walks you through your best-matched offers.",
          },
        ],
        callouts: [
          {
            icon: "🎯",
            tone: "blue",
            text: [
              { text: "One check, many offers.", bold: true },
              {
                text: " No obligation, no impact on your score, and no juggling appointments.",
              },
            ],
          },
          {
            icon: "💰",
            tone: "green",
            text: [
              { text: "100% free to compare.", bold: true },
              {
                text: " No loan broker fee, no service charge — you're never charged for matching or advice. SmartLend is paid by the lenders, not you.",
              },
            ],
          },
        ],
        note: [
          { text: "Prefer a fully hands-on, " },
          { text: "done-for-you", bold: true },
          { text: " experience? Our optional " },
          { text: "Concierge service", bold: true },
          {
            text: " handles everything end-to-end for a transparent, agreed-upfront fee — entirely your choice, never required.",
          },
        ],
        actions: [
          {
            label: "Show me what I qualify for",
            value: "yes_compare",
            style: "primary",
            nextScreen: 3,
          },
          {
            label: "Maybe later →",
            value: "maybe_later",
            style: "link",
            nextScreen: 3,
          },
        ],
      },
    ],
    completion: {
      eyebrow: "✓ All done",
      title: "Your CBS code is ready.",
      subtitle:
        "Use the code below to retrieve your full CBS report. We'll walk you through it on the next step.",
      trustBadges: ["Banks & lenders", "Bank-level security", "No hidden fees"],
      button: "Continue to my report",
      fine:
        "Your data is encrypted end-to-end and only shared with lenders you choose.",
      showCopyButton: true,
    },
  },
  classic: {
    questions: [
      {
        id: "reason",
        title: "What brings you here today?",
        subtitle:
          "Help us understand your financing needs so we can show you the best options.",
        options: [
          {
            value: "property",
            icon: "🏠",
            title: "Home or Property Financing",
          },
          {
            value: "business",
            icon: "💼",
            title: "Business Financing",
          },
          {
            value: "personal",
            icon: "💳",
            title: "Personal Financing",
          },
          {
            value: "credit_health",
            icon: "📊",
            title: "Just checking my credit profile",
          },
        ],
      },
      {
        id: "second_opinion",
        title: "Get expert guidance",
        subtitle:
          "Most people find better rates and terms when they compare multiple options. Would you like us to review your options with a quick financial health check?",
        infoBox: [
          { text: "💡 Pro tip:", bold: true },
          {
            text: " A second opinion takes just 2 minutes and could save you thousands of dollars.",
          },
        ],
        actions: [
          {
            label: "✓ Yes, show me my options",
            value: "yes_second_opinion",
            style: "primary",
            nextScreen: 2,
          },
          {
            label: "Maybe Later",
            value: "maybe_later",
            style: "secondary",
            nextScreen: 2,
          },
        ],
      },
      {
        id: "compare_options",
        title: "Let's find what you qualify for",
        subtitle:
          "SmartLend works with Singapore's top licensed lenders. We'll compare your options in one place so you can make the best choice.",
        infoBox: [
          { text: "🔒 Safe & Secure:", bold: true },
          {
            text: " Your data is encrypted and only shared with lenders you choose.",
          },
        ],
        actions: [
          {
            label: "See My Options",
            value: "yes_compare",
            style: "primary",
            nextScreen: 3,
          },
          {
            label: "Maybe Later",
            value: "maybe_later",
            style: "link",
            nextScreen: 3,
          },
        ],
      },
    ],
    completion: {
      title: "You're all set!",
      subtitle:
        "Use your CBS code to retrieve your full credit report and financing options:",
      helper:
        "Follow the instructions on the next page to retrieve your CBS report. Keep this code safe for future reference.",
      trustBadges: ["Licensed Lenders", "Instant Results", "No Hidden Fees"],
      button: "Continue to Report",
      showCopyButton: false,
    },
  },
};

const cbsModalStyles = {
  modern: {
    shell: {
      borderRadius: "var(--br-card-radius, 22px)",
      overflow: "hidden",
      boxShadow: "var(--br-shadow, 0 30px 70px -20px rgba(13, 38, 90, 0.35))",
      background: "var(--br-surface, #ffffff)",
      fontFamily: "Inter, Manrope, sans-serif",
    },
    header: {
      padding: "20px 22px 14px",
      borderBottom: "1px solid #E7ECF3",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    logo: {
      width: 40,
      height: 40,
      borderRadius: "var(--br-card-radius, 12px)",
      background: "linear-gradient(135deg, var(--br-primary, #1552F0) 0%, #3f74ff 100%)",
      color: "#fff",
      display: "grid",
      placeItems: "center",
      fontWeight: 900,
      fontSize: 20,
      boxShadow: "var(--br-shadow, 0 8px 18px -6px rgba(21,82,240,.6))",
    },
    body: { padding: "18px 22px 24px" },
    eyebrow: {
      display: "inline-flex",
      color: "var(--br-primary, #0B3AC2)",
      background: "var(--br-soft, #EAF0FF)",
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0,
      marginBottom: 12,
    },
    title: {
      color: "#0B1220",
      fontSize: 21,
      lineHeight: 1.25,
      letterSpacing: 0,
      fontWeight: 900,
      margin: "0 0 8px",
    },
    subtitle: {
      color: "#334155",
      fontSize: 14,
      lineHeight: 1.55,
      margin: 0,
    },
    option: {
      border: "1.5px solid #E7ECF3",
      background: "var(--br-surface, #ffffff)",
      borderRadius: "var(--br-card-radius, 14px)",
      padding: "13px 14px",
    },
    optionActive: {
      border: "1.5px solid var(--br-primary, #1552F0)",
      background: "var(--br-soft, #EAF0FF)",
      boxShadow: "var(--br-shadow, 0 6px 16px -8px rgba(21,82,240,.4))",
    },
    icon: {
      width: 40,
      height: 40,
      borderRadius: "var(--br-card-radius, 11px)",
      background: "var(--br-soft, #EAF0FF)",
      display: "grid",
      placeItems: "center",
      fontSize: 19,
      flex: "0 0 auto",
    },
    primary:
      "linear-gradient(135deg, #1552F0 0%, #2f6bff 100%)",
    primaryShadow: "0 14px 26px -12px rgba(21,82,240,.75)",
    accent: "#1552F0",
    soft: "#F5F8FD",
    callout: "#E4F8F0",
    calloutBorder: "#c8efdd",
  },
  classic: {
    shell: {
      borderRadius: "var(--br-card-radius, 16px)",
      overflow: "hidden",
      boxShadow: "var(--br-shadow, 0 20px 60px rgba(0, 0, 0, 0.2))",
      background: "var(--br-surface, #ffffff)",
      fontFamily: "Inter, Manrope, sans-serif",
    },
    header: {
      padding: "28px 24px 20px",
      textAlign: "center",
      color: "#fff",
      background: "linear-gradient(135deg, var(--br-primary, #667eea) 0%, #764ba2 100%)",
    },
    logo: null,
    body: { padding: "28px 24px 24px" },
    eyebrow: {
      display: "inline-flex",
      color: "var(--br-primary, #667eea)",
      background: "var(--br-soft, #f0f4ff)",
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0,
      marginBottom: 12,
    },
    title: {
      color: "#1a1a1a",
      fontSize: 20,
      lineHeight: 1.32,
      fontWeight: 800,
      margin: "0 0 10px",
    },
    subtitle: {
      color: "#666",
      fontSize: 14,
      lineHeight: 1.6,
      margin: 0,
    },
    option: {
      border: "2px solid #e5e7eb",
      background: "#fafafa",
      borderRadius: "var(--br-card-radius, 10px)",
      padding: "14px 16px",
    },
    optionActive: {
      border: "2px solid var(--br-primary, #667eea)",
      background: "var(--br-soft, #f0f4ff)",
    },
    icon: {
      width: 34,
      height: 34,
      borderRadius: "var(--br-card-radius, 10px)",
      background: "var(--br-soft, #f0f4ff)",
      display: "grid",
      placeItems: "center",
      fontSize: 18,
      flex: "0 0 auto",
    },
    primary:
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    primaryShadow: "0 8px 16px rgba(102, 126, 234, 0.4)",
    accent: "#667eea",
    soft: "#f0f4ff",
    callout: "#e7f5ff",
    calloutBorder: "#667eea",
  },
};

const CheckMark = ({ color = "#fff" }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12l5 5L20 6"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const renderCbsRichText = (content) => {
  if (!Array.isArray(content)) return content;

  return content.map((part, index) =>
    part.bold ? (
      <b key={`${part.text}-${index}`}>{part.text}</b>
    ) : (
      <React.Fragment key={`${part.text}-${index}`}>
        {part.text}
      </React.Fragment>
    )
  );
};

const CBS_CAMPAIGN_LEAD_ENDPOINT =
  "https://www.smart-lend.com/api/landing-page-leads/upsert";
const CBS_POST_QUESTIONNAIRE_ENDPOINT = `${API_BASE}/campaign/cbs-questionnaire-submissions`;
const CBS_INTENT_ENDPOINT = `${API_BASE}/campaign/cbs-intent`;
const CBS_CAMPAIGN_PATH = "/b-cbs-campaign";
const LANDING_PAGE_VISITOR_COOKIE = "landing_page_visitor_token";
const CBS_REFI_RATE = 1.35;

const safeStorageGet = (storage, key) => {
  try {
    return storage?.getItem(key) || null;
  } catch (error) {
    return null;
  }
};

const createVisitorToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `lpl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatCbsCurrency = (value) =>
  new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const parseCbsCurrencyInput = (value) => {
  const numeric = Number(String(value || "").replace(/[^0-9]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const monthlyRepayment = (principal, annualRate, years) => {
  const monthlyRate = annualRate / 100 / 12;
  const months = years * 12;

  if (monthlyRate === 0) return principal / months;
  return (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
};

const getCampaignPanelStyle = () => ({
  width: "min(860px, 100%)",
  background: "var(--br-surface, #FFFFFF)",
  border: "1px solid #E7ECF3",
  borderRadius: "var(--br-card-radius, 20px)",
  padding:
    typeof window !== "undefined" && window.innerWidth > 1200
      ? "24px"
      : "18px",
  boxShadow: "var(--br-shadow, 0 22px 55px -34px rgba(13, 38, 90, 0.55))",
  fontFamily: "Inter, Manrope, sans-serif",
});

const cbsFieldLabelStyle = {
  color: "#334155",
  fontSize: 13,
  fontWeight: 800,
  marginBottom: 8,
};

const cbsTextInputStyle = {
  width: "100%",
  border: "1.5px solid #E7ECF3",
  borderRadius: 12,
  padding: "12px 14px",
  fontSize: 15,
  fontFamily: "inherit",
  outline: "none",
};

const CbsRefiSavingsSimulator = ({ answers, onLeave, onStart, onSubmit }) => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [oldRate, setOldRate] = useState(4);
  const [tenure, setTenure] = useState(25);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedAtRef = useRef(Date.now());
  const submittedRef = useRef(false);

  useEffect(() => {
    onStart?.({
      form_type: "property_refinancing",
      questionnaire_answers: answers,
    });

    const trackLeave = () => {
      if (submittedRef.current) return;
      onLeave?.({
        form_type: "property_refinancing",
        duration_ms: Date.now() - startedAtRef.current,
        questionnaire_answers: answers,
      });
    };

    window.addEventListener("pagehide", trackLeave);
    return () => {
      window.removeEventListener("pagehide", trackLeave);
      trackLeave();
    };
  }, [answers, onLeave, onStart]);

  const savings = useMemo(() => {
    const currentMonthly = monthlyRepayment(loanAmount, oldRate, tenure);
    const refiMonthly = monthlyRepayment(loanAmount, CBS_REFI_RATE, tenure);
    const monthlySavings = Math.max(0, currentMonthly - refiMonthly);
    const percentage =
      currentMonthly > 0 ? (monthlySavings / currentMonthly) * 100 : 0;

    return {
      currentMonthly,
      refiMonthly,
      monthlySavings,
      totalSavings: monthlySavings * tenure * 12,
      percentage,
      percentageLabel: `${percentage.toFixed(percentage >= 10 ? 0 : 1)}%`,
    };
  }, [loanAmount, oldRate, tenure]);

  const submitOptions = async () => {
    if (submitted || isSubmitting) return;

    setIsSubmitting(true);
    const saved = await onSubmit({
      type: "property_refinancing",
      loan_amount: loanAmount,
      old_rate: oldRate,
      refi_rate: CBS_REFI_RATE,
      remaining_tenure: tenure,
      estimated_monthly_savings: Math.round(savings.monthlySavings),
      estimated_total_savings: Math.round(savings.totalSavings),
      duration_ms: Date.now() - startedAtRef.current,
      questionnaire_answers: answers,
    });
    submittedRef.current = Boolean(saved);
    setSubmitted(Boolean(saved));
    setIsSubmitting(false);
  };

  return (
    <div style={getCampaignPanelStyle()}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "#E4F8F0",
          color: "#0a7d57",
          borderRadius: 999,
          padding: "6px 10px",
          fontSize: 12,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0,
        }}
      >
        Before you continue
      </div>
      <div
        style={{
          color: "#6B7A90",
          fontSize: 12,
          fontWeight: 800,
          marginTop: 14,
        }}
      >
        You chose Home / Property financing
      </div>
      <h2
        style={{
          color: "#0B1220",
          fontSize: window.innerWidth > 1200 ? 28 : 22,
          lineHeight: 1.2,
          fontWeight: 900,
          margin: "8px 0 8px",
          letterSpacing: 0,
        }}
      >
        Took a property loan 2 years ago? At today's refinancing rates you
        could be saving{" "}
        <span style={{ color: "#12B981" }}>{savings.percentageLabel}</span>.
      </h2>
      <p
        style={{
          color: "#334155",
          fontSize: 14,
          lineHeight: "22px",
          margin: 0,
          maxWidth: 620,
        }}
      >
        Rates have moved. Slide in your numbers to estimate what refinancing
        this year could put back in your pocket.
      </p>

      <div
        style={{
          marginTop: 18,
          borderRadius: "var(--br-card-radius, 18px)",
          padding: 20,
          background: "linear-gradient(135deg, #06301f 0%, #0d7a52 100%)",
          color: "#fff",
          boxShadow: "var(--br-shadow, 0 22px 40px -22px rgba(9,110,72,.9))",
        }}
      >
        <div
          style={{
            fontSize: 11.5,
            letterSpacing: 0,
            textTransform: "uppercase",
            color: "#b6f0d7",
            fontWeight: 800,
          }}
        >
          Estimated savings on your monthly repayment
        </div>
        <div
          style={{
            fontSize: window.innerWidth > 1200 ? 52 : 42,
            fontWeight: 900,
            lineHeight: 1,
            marginTop: 8,
          }}
        >
          {savings.percentageLabel}
        </div>
        <div style={{ fontSize: 13, color: "#d6f7e9", marginTop: 6 }}>
          About {formatCbsCurrency(savings.monthlySavings)}/mo lighter, or{" "}
          {formatCbsCurrency(savings.totalSavings)} over your remaining tenure
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              window.innerWidth > 720 ? "repeat(3, 1fr)" : "1fr",
            gap: 10,
            marginTop: 16,
          }}
        >
          {[
            ["Now / month", savings.currentMonthly],
            ["After refi", savings.refiMonthly],
            ["You save / month", savings.monthlySavings],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                background: "rgba(255,255,255,.1)",
                border: "1px solid rgba(255,255,255,.16)",
                borderRadius: "var(--br-card-radius, 12px)",
                padding: "11px 12px",
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: 0,
                  textTransform: "uppercase",
                  color: "#a9e6cd",
                  fontWeight: 800,
                }}
              >
                {label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 3 }}>
                {formatCbsCurrency(value)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
        <label>
          <div style={cbsFieldLabelStyle}>
            Outstanding loan amount{" "}
            <span style={{ color: "var(--br-primary, #0B3AC2)" }}>
              {formatCbsCurrency(loanAmount)}
            </span>
          </div>
          <input
            type="range"
            min="100000"
            max="2000000"
            step="10000"
            value={loanAmount}
            onChange={(event) => setLoanAmount(Number(event.target.value))}
            style={{ width: "100%" }}
          />
        </label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth > 720 ? "1fr 1fr" : "1fr",
            gap: 14,
          }}
        >
          <label>
            <div style={cbsFieldLabelStyle}>
              Your rate 2 yrs ago{" "}
              <span style={{ color: "var(--br-primary, #0B3AC2)" }}>
                {oldRate.toFixed(2)}%
              </span>
            </div>
            <input
              type="range"
              min="1.5"
              max="5"
              step="0.05"
              value={oldRate}
              onChange={(event) => setOldRate(Number(event.target.value))}
              style={{ width: "100%" }}
            />
          </label>
          <div
            style={{
              background: "#E4F8F0",
              border: "1px solid #c8efdd",
              borderRadius: "var(--br-card-radius, 14px)",
              padding: "12px 14px",
            }}
          >
            <div style={{ color: "#0a7d57", fontSize: 12, fontWeight: 900 }}>
              Refi lowest rate today
            </div>
            <div style={{ color: "#0a7d57", fontSize: 28, fontWeight: 900 }}>
              {CBS_REFI_RATE.toFixed(2)}%
            </div>
            <div style={{ color: "#17a06d", fontSize: 11, fontWeight: 700 }}>
              Estimate only. Consultant will confirm your exact package.
            </div>
          </div>
        </div>
        <label>
          <div style={cbsFieldLabelStyle}>
            Remaining tenure{" "}
            <span style={{ color: "var(--br-primary, #0B3AC2)" }}>
              {tenure} {tenure === 1 ? "year" : "years"}
            </span>
          </div>
          <input
            type="range"
            min="5"
            max="30"
            step="1"
            value={tenure}
            onChange={(event) => setTenure(Number(event.target.value))}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <div
        style={{
          marginTop: 18,
          background: "var(--br-soft, #EAF0FF)",
          border: "1px solid #cfdcff",
          borderRadius: "var(--br-card-radius, 14px)",
          padding: "13px 14px",
          color: "#334155",
          fontSize: 13,
          lineHeight: "20px",
        }}
      >
        This is just an estimate. SmartLend compares live refinancing packages
        and an advisor confirms your exact savings free of charge.
      </div>

      <button
        type="button"
        className="mui_button"
        onClick={submitOptions}
        disabled={submitted || isSubmitting}
        style={{
          width: "100%",
          marginTop: 18,
          border: "none",
          borderRadius: "var(--br-card-radius, 14px)",
          padding: "13px 18px",
          color: "#fff",
          background: "linear-gradient(135deg, var(--br-primary, #1552F0) 0%, var(--br-primary, #2f6bff) 100%)",
          boxShadow: "var(--br-shadow, 0 14px 26px -12px rgba(21,82,240,.75))",
          fontWeight: 900,
          fontFamily: "inherit",
          cursor: submitted || isSubmitting ? "not-allowed" : "pointer",
          opacity: submitted || isSubmitting ? 0.72 : 1,
        }}
      >
        {isSubmitting ? "Submitting..." : "Get my refinancing options"}
      </button>
      {submitted ? (
        <div
          style={{
            color: "#0a7d57",
            fontSize: 13,
            fontWeight: 800,
            textAlign: "center",
            marginTop: 12,
          }}
        >
          Thanks. Our consultants will reach out within 24 hours.
        </div>
      ) : null}
    </div>
  );
};

const CBS_FINANCING_PURPOSE_OPTIONS = [
  "Working capital / cash flow",
  "Business expansion",
  "Inventory / stock purchase",
  "Equipment / machinery",
  "Renovation / fit-out",
  "Marketing & hiring",
  "Pay suppliers / vendors",
  "Refinance existing loan",
  "Commercial property purchase",
  "Bridging / short-term needs",
  "Others",
];

const CbsLoanPreferenceQuestionnaire = ({ answers, onLeave, onStart, onSubmit }) => {
  const history = useHistory();
  const [borrowerType, setBorrowerType] = useState("company");
  const [amount, setAmount] = useState(50000);
  const [purpose, setPurpose] = useState(CBS_FINANCING_PURPOSE_OPTIONS[0]);
  const [tenure, setTenure] = useState(12);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingChoice, setSubmittingChoice] = useState(null);
  const startedAtRef = useRef(Date.now());
  const submittedRef = useRef(false);

  useEffect(() => {
    onStart?.({
      form_type: "loan_options_request",
      questionnaire_answers: answers,
    });

    const trackLeave = () => {
      if (submittedRef.current) return;
      onLeave?.({
        form_type: "loan_options_request",
        duration_ms: Date.now() - startedAtRef.current,
        questionnaire_answers: answers,
      });
    };

    window.addEventListener("pagehide", trackLeave);
    return () => {
      window.removeEventListener("pagehide", trackLeave);
      trackLeave();
    };
  }, [answers, onLeave, onStart]);

  const submitLoanRequest = async (continuationChoice) => {
    if (submitted || isSubmitting) return;

    setIsSubmitting(true);
    setSubmittingChoice(continuationChoice);
    const saved = await onSubmit({
      type: "loan_options_request",
      borrower_type: borrowerType,
      loan_amount: amount,
      purpose_of_funds: purpose,
      tenure_months: tenure,
      continuation_choice: continuationChoice,
      duration_ms: Date.now() - startedAtRef.current,
      questionnaire_answers: answers,
    });
    submittedRef.current = Boolean(saved);
    setSubmitted(Boolean(saved));
    setIsSubmitting(false);
    setSubmittingChoice(null);

    if (saved && continuationChoice === "start_application") {
      history.push(borrowerType === "personal" ? "/b-personal-loans" : "/b-loan-hub");
    }
  };

  return (
    <div
      style={{
        ...getCampaignPanelStyle(),
        width: "min(760px, 100%)",
        border: "none",
        borderRadius: "var(--br-card-radius, 22px)",
        padding: window.innerWidth > 720 ? "34px 38px" : "26px 22px",
        boxShadow: "var(--br-shadow, 0 18px 50px rgba(15,27,61,.08))",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          background: "#EEF2FF",
          color: "var(--br-primary, #1D4ED8)",
          borderRadius: 999,
          padding: "7px 14px",
          fontSize: 12,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        Financing preference
      </div>
      <h2
        style={{
          color: "#0B1220",
          fontSize: window.innerWidth > 560 ? 30 : 24,
          lineHeight: 1.18,
          fontWeight: 900,
          margin: "16px 0 12px",
          letterSpacing: 0,
        }}
      >
        Earlier you mentioned you'd like to see your options.
      </h2>

      <div
        style={{
          background: "linear-gradient(90deg,#eff4ff,#f6f9ff)",
          border: "1px solid #dbe4ff",
          borderLeft: "4px solid #2563eb",
          borderRadius: "var(--br-card-radius, 12px)",
          padding: "15px 17px",
          color: "#1e2a5a",
          fontSize: 14.5,
          lineHeight: "22px",
          margin: "0 0 24px",
        }}
      >
        Recently rejected — or not sure you're getting the best options for
        your financing needs? We compare <strong>20+ lenders</strong> to find
        the route that actually fits, <strong>no obligation</strong>.
      </div>

      <p
        style={{
          color: "#6B7280",
          fontSize: 15,
          lineHeight: "22px",
          margin: "0 0 26px",
          maxWidth: 620,
        }}
      >
        Share your financing type, amount, purpose and tenor below. Our
        consultants will review it and reach out within <strong>24 hours</strong>{" "}
        with your best route — before rates move.
      </p>

      <div style={{ display: "grid", gap: 0 }}>
        <div>
          <div style={cbsFieldLabelStyle}>Financing under</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                window.innerWidth > 720 ? "1fr 1fr" : "1fr",
              gap: 10,
            }}
          >
            {[
              ["personal", "Personal"],
              ["company", "Company"],
            ].map(([value, label]) => {
              const active = borrowerType === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBorrowerType(value)}
                  style={{
                    border: active ? "1.5px solid var(--br-primary, #3B82F6)" : "1.5px solid #E5E7EB",
                    background: active ? "#EEF2FF" : "var(--br-surface, #fff)",
                    color: "#0B1220",
                    borderRadius: "var(--br-card-radius, 12px)",
                    padding: "15px",
                    fontSize: 15,
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .15s ease",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <label>
          <div style={{ ...cbsFieldLabelStyle, marginTop: 22 }}>Amount</div>
          <input
            type="text"
            inputMode="numeric"
            value={formatCbsCurrency(amount)}
            onChange={(event) =>
              setAmount(parseCbsCurrencyInput(event.target.value))
            }
            style={cbsTextInputStyle}
          />
        </label>

        <label>
          <div style={{ ...cbsFieldLabelStyle, marginTop: 22 }}>
            Purpose of funds
          </div>
          <select
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            style={cbsTextInputStyle}
          >
            {CBS_FINANCING_PURPOSE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <div
            style={{
              color: "#6B7280",
              fontSize: 12.5,
              lineHeight: "18px",
              marginTop: 7,
            }}
          >
            Telling us the purpose helps us match you to lenders who fund it best.
          </div>
        </label>

        <label>
          <div style={{ ...cbsFieldLabelStyle, marginTop: 22 }}>Tenor</div>
          <select
            value={tenure}
            onChange={(event) => setTenure(Number(event.target.value))}
            style={cbsTextInputStyle}
          >
            {[6, 12, 18, 24, 36, 48, 60].map((month) => (
              <option key={month} value={month}>
                {month} months
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        style={{
          margin: "26px 0 18px",
          background: "#E9F7EF",
          border: "1px solid #C7ECD6",
          borderRadius: "var(--br-card-radius, 12px)",
          padding: "15px 16px",
          color: "#3A6B52",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: "21px",
        }}
      >
        Submit now and a consultant will reach out within{" "}
        <strong>24 hours</strong> with your matched options —{" "}
        <strong>no obligation</strong>, and it won't affect your credit score.
      </div>

      <div
        style={{
          textAlign: "center",
          color: "#374151",
          fontSize: 13,
          fontWeight: 900,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          margin: "4px 0 16px",
        }}
      >
        Choose how you'd like to continue
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: window.innerWidth > 720 ? "1fr 1fr" : "1fr",
          gap: 18,
        }}
      >
        <div
          style={{
            border: "1px solid #E5E7EB",
            borderRadius: "var(--br-card-radius, 18px)",
            padding: "26px 24px 24px",
            background: "var(--br-surface, #fff)",
            minHeight: 238,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h3
              style={{
                color: "#0B1220",
                fontSize: 18,
                fontWeight: 900,
                margin: "0 0 8px",
                letterSpacing: 0,
              }}
            >
              Let a specialist guide me
            </h3>
            <p
              style={{
                color: "#6B7280",
                fontSize: 14.5,
                lineHeight: "23px",
                margin: 0,
              }}
            >
              Not sure which route fits? A loan specialist will review your
              profile and recommend your best option — <strong>free</strong>,
              within <strong>24 hours</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => submitLoanRequest("consultant_call")}
            disabled={submitted || isSubmitting}
            style={{
              width: "100%",
              border: "1px solid #DCE6F7",
              borderRadius: "var(--br-card-radius, 14px)",
              padding: "15px 16px",
              color: "var(--br-primary, #1D4ED8)",
              background: "#EEF4FF",
              fontWeight: 900,
              fontSize: 15,
              fontFamily: "inherit",
              cursor: submitted || isSubmitting ? "not-allowed" : "pointer",
              opacity: submitted || isSubmitting ? 0.72 : 1,
              marginTop: 26,
            }}
          >
            {isSubmitting && submittingChoice === "consultant_call"
              ? "Submitting..."
              : "Have a Consultant Call Me →"}
          </button>
        </div>

        <div
          style={{
            border: "1.5px solid #2F80ED",
            borderRadius: "var(--br-card-radius, 18px)",
            padding: "26px 24px 24px",
            background: "linear-gradient(180deg,#f8fbff,#ffffff)",
            boxShadow: "var(--br-shadow, 0 16px 34px rgba(37,99,235,.12))",
            minHeight: 238,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -12,
              left: 24,
              background: "var(--br-primary, #1D4ED8)",
              color: "#fff",
              borderRadius: 999,
              padding: "4px 14px",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            Fastest
          </div>
          <div>
            <h3
              style={{
                color: "#0B1220",
                fontSize: 18,
                fontWeight: 900,
                margin: "0 0 10px",
                letterSpacing: 0,
              }}
            >
              Submit my application now
            </h3>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#EEF4FF",
                color: "var(--br-primary, #1D4ED8)",
                borderRadius: "var(--br-card-radius, 8px)",
                padding: "9px 12px",
                fontSize: 13,
                fontWeight: 900,
                marginBottom: 16,
              }}
            >
              <span>⚡ 3 simple steps</span>
              <span style={{ color: "#93A4BD" }}>•</span>
              <span>Under 3 minutes</span>
            </div>
            <p
              style={{
                color: "#6B7280",
                fontSize: 14.5,
                lineHeight: "23px",
                margin: 0,
              }}
            >
              Ready to go? Just <strong>3 steps, done in under 3 minutes</strong>{" "}
              — we'll fast-track your file to matched lenders. The quickest
              path to funding, no obligation.
            </p>
          </div>
          <button
            type="button"
            className="mui_button"
            onClick={() => submitLoanRequest("start_application")}
            disabled={submitted || isSubmitting}
            style={{
              width: "100%",
              marginTop: 22,
              border: "none",
              borderRadius: "var(--br-card-radius, 14px)",
              padding: "15px 16px",
              color: "#fff",
              background: "linear-gradient(90deg,var(--br-primary, #2563eb),var(--br-primary, #1d4ed8))",
              boxShadow: "var(--br-shadow, 0 10px 24px rgba(37,99,235,.28))",
              fontWeight: 900,
              fontSize: 15,
              fontFamily: "inherit",
              cursor: submitted || isSubmitting ? "not-allowed" : "pointer",
              opacity: submitted || isSubmitting ? 0.72 : 1,
            }}
          >
            {isSubmitting && submittingChoice === "start_application"
              ? "Submitting..."
              : "Start My 3-Min Application →"}
          </button>
        </div>
      </div>

      <p
        style={{
          textAlign: "center",
          color: "#6B7280",
          fontSize: 12.5,
          margin: "18px 0 0",
        }}
      >
        🔒 Either way, it won't affect your credit score. Your details are shared
        only with matched lenders.
      </p>
      {submitted ? (
        <div
          style={{
            color: "#0a7d57",
            fontSize: 13,
            fontWeight: 800,
            textAlign: "center",
            marginTop: 12,
          }}
        >
          Request submitted. Our consultants will reach out within 24 hours.
        </div>
      ) : null}
    </div>
  );
};

const CbsIntentQuestionnaireModal = ({
  open,
  variant,
  cbsCode,
  onComplete,
  onAnalyticsEvent,
}) => {
  const [screen, setScreen] = useState(0);
  const [answers, setAnswers] = useState({});
  const startedAtRef = useRef(null);
  const lastTrackedScreenRef = useRef(null);
  const theme = cbsModalStyles[variant] || cbsModalStyles.modern;
  const isModern = variant === "modern";
  const flow = cbsIntentFlows[variant] || cbsIntentFlows.modern;
  const currentQuestion = flow.questions[screen];
  const progressScreens = flow.questions.length + 1;
  const completion = flow.completion;

  useEffect(() => {
    if (open) {
      setScreen(0);
      setAnswers({});
      startedAtRef.current = Date.now();
      lastTrackedScreenRef.current = null;
      onAnalyticsEvent?.("cbs_questionnaire_started", {
        variant,
      });
    } else {
      startedAtRef.current = null;
      lastTrackedScreenRef.current = null;
    }
  }, [onAnalyticsEvent, open, variant]);

  useEffect(() => {
    if (!open || screen >= flow.questions.length) return;
    if (lastTrackedScreenRef.current === screen) return;

    lastTrackedScreenRef.current = screen;
    const question = flow.questions[screen];
    onAnalyticsEvent?.("cbs_questionnaire_screen_view", {
      variant,
      screen_index: screen,
      screen_number: screen + 1,
      question_id: question.id,
      question_title: question.title,
    });
  }, [flow.questions, onAnalyticsEvent, open, screen, variant]);

  const chooseAnswer = (questionId, value, label) => {
    setAnswers((previous) => ({ ...previous, [questionId]: value }));
    onAnalyticsEvent?.("cbs_questionnaire_answer", {
      variant,
      screen_index: screen,
      screen_number: screen + 1,
      question_id: questionId,
      answer_value: value,
      answer_label: label || value,
    });
  };

  const finish = () => {
    onComplete({
      ...answers,
      completed_at: new Date().toISOString(),
      duration_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
    });
  };

  const renderProgress = () => {
    if (isModern) {
      return (
        <div style={{ display: "flex", gap: 6, padding: "14px 22px 0" }}>
          {Array.from({ length: progressScreens }).map((_, index) => (
            <div
              key={index}
              style={{
                height: 4,
                flex: 1,
                borderRadius: 99,
                background: "#E7ECF3",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: index <= screen ? "100%" : "0%",
                  borderRadius: 99,
                  background: "linear-gradient(90deg, var(--br-primary, #1552F0), #12B981)",
                  transition: "width .3s ease",
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        style={{
          height: 3,
          background: "var(--br-surface, rgba(255,255,255,0.3))",
          marginTop: 16,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((screen + 1) / progressScreens) * 100}%`,
            background: "var(--br-surface, #fff)",
            transition: "width .3s ease",
          }}
        />
      </div>
    );
  };

  const primaryButtonStyle = {
    width: "100%",
    border: "none",
    borderRadius: isModern ? 13 : 8,
    padding: "13px 18px",
    color: "#fff",
    background: theme.primary,
    boxShadow: theme.primaryShadow,
    cursor: "pointer",
    fontWeight: 800,
    fontFamily: "inherit",
  };

  const secondaryButtonStyle = {
    width: "100%",
    border: isModern ? "1.5px solid #E7ECF3" : "2px solid #e5e7eb",
    borderRadius: isModern ? 13 : 8,
    padding: "13px 18px",
    color: isModern ? "#334155" : theme.accent,
    background: "var(--br-surface, #fff)",
    cursor: "pointer",
    fontWeight: 800,
    fontFamily: "inherit",
  };

  const linkButtonStyle = {
    width: "100%",
    border: "none",
    borderRadius: isModern ? 13 : 8,
    padding: "12px 18px",
    color: theme.accent,
    background: "transparent",
    cursor: "pointer",
    fontWeight: 800,
    fontFamily: "inherit",
  };

  const getActionButtonStyle = (actionStyle) => {
    if (actionStyle === "primary") return primaryButtonStyle;
    if (actionStyle === "link") return linkButtonStyle;
    return secondaryButtonStyle;
  };

  return (
    <Modal
      className="cbs-intent-questionnaire-modal"
      open={open}
      footer={null}
      closable={false}
      maskClosable={false}
      width={460}
      centered
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      <style>
        {`
          .cbs-intent-questionnaire-modal .ant-modal-content {
            padding: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
          }
          .cbs-intent-questionnaire-modal .ant-modal-body {
            padding: 0 !important;
          }
        `}
      </style>
      <div style={theme.shell}>
        {isModern ? (
          <>
            <div style={theme.header}>
              <div style={theme.logo}>S</div>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>SmartLend</div>
                <div
                  style={{
                    color: "#6B7A90",
                    fontSize: 11.5,
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  Compare banks & lenders · Singapore
                </div>
              </div>
            </div>
            {renderProgress()}
          </>
        ) : (
          <div style={theme.header}>
            <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 6 }}>
              Quick CBS Check
            </div>
            <div style={{ fontSize: 14, opacity: 0.95, fontWeight: 600 }}>
              Get instant financing options tailored for you
            </div>
            {renderProgress()}
          </div>
        )}

        <div style={theme.body}>
          {screen < flow.questions.length ? (
            <>
              {currentQuestion.eyebrow ? (
                <span style={theme.eyebrow}>{currentQuestion.eyebrow}</span>
              ) : null}
              <h1 style={theme.title}>{currentQuestion.title}</h1>
              <p style={theme.subtitle}>
                {renderCbsRichText(currentQuestion.subtitle)}
              </p>

              {screen === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginTop: 18,
                  }}
                >
                  {currentQuestion.options.map((option) => {
                    const active = answers.reason === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          chooseAnswer("reason", option.value, option.title)
                        }
                        style={{
                          ...theme.option,
                          ...(active ? theme.optionActive : {}),
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          textAlign: "left",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        <span style={theme.icon}>{option.icon}</span>
                        <span style={{ flex: 1 }}>
                          <span
                            style={{
                              display: "block",
                              color: "#0B1220",
                              fontSize: 14.5,
                              fontWeight: 800,
                              lineHeight: 1.35,
                            }}
                          >
                            {option.title}
                          </span>
                          {option.description ? (
                            <span
                              style={{
                                display: "block",
                                color: "#6B7A90",
                                fontSize: 12,
                                marginTop: 3,
                                lineHeight: 1.4,
                              }}
                            >
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            border: active
                              ? `2px solid ${theme.accent}`
                              : "2px solid #E7ECF3",
                            background: active ? theme.accent : "var(--br-surface, #fff)",
                            flex: "0 0 auto",
                          }}
                        >
                          {active ? <CheckMark /> : null}
                        </span>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    disabled={!answers.reason}
                    onClick={() => setScreen(1)}
                    style={{
                      ...primaryButtonStyle,
                      marginTop: 12,
                      opacity: answers.reason ? 1 : 0.45,
                      cursor: answers.reason ? "pointer" : "not-allowed",
                    }}
                  >
                    Continue
                  </button>
                </div>
              ) : (
                <>
                  {currentQuestion.infoBox ? (
                    <div
                      style={{
                        background: "#e7f5ff",
                        borderLeft: `4px solid ${theme.accent}`,
                        padding: "12px 16px",
                        borderRadius: 6,
                        fontSize: 13,
                        color: "#333",
                        lineHeight: 1.5,
                        marginTop: 16,
                      }}
                    >
                      {renderCbsRichText(currentQuestion.infoBox)}
                    </div>
                  ) : null}
                  {currentQuestion.callouts?.map((callout, index) => (
                    <div
                      key={`${callout.icon}-${index}`}
                      style={{
                        display: "flex",
                        gap: 11,
                        alignItems: "flex-start",
                        background:
                          callout.tone === "blue" ? "var(--br-soft, #EAF0FF)" : theme.callout,
                        border: `1px solid ${
                          callout.tone === "blue" ? "#cfdcff" : theme.calloutBorder
                        }`,
                        padding: "13px 14px",
                        borderRadius: "var(--br-card-radius, 14px)",
                        marginTop: index === 0 ? 18 : 10,
                        color: "#334155",
                        fontSize: 12.8,
                        lineHeight: 1.5,
                      }}
                    >
                      <span>{callout.icon}</span>
                      <span>{renderCbsRichText(callout.text)}</span>
                    </div>
                  ))}
                  {currentQuestion.proof ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 16,
                      }}
                    >
                      <div style={{ display: "flex" }}>
                        {currentQuestion.proof.avatars.map((avatar, index) => (
                          <span
                            key={`${avatar}-${index}`}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              border: "2px solid #fff",
                              marginLeft: index === 0 ? 0 : -8,
                              background: ["#6366f1", "#12b981", "#f5a623", "#ec4899"][
                                index % 4
                              ],
                              display: "grid",
                              placeItems: "center",
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#fff",
                            }}
                          >
                            {avatar}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 12, color: "#6B7A90" }}>
                        {renderCbsRichText(currentQuestion.proof.text)}
                      </div>
                    </div>
                  ) : null}
                  {currentQuestion.note ? (
                    <p
                      style={{
                        fontSize: 11.5,
                        lineHeight: 1.55,
                        color: "#6B7A90",
                        marginTop: 12,
                        marginBottom: 0,
                      }}
                    >
                      {renderCbsRichText(currentQuestion.note)}
                    </p>
                  ) : null}

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginTop: 22,
                    }}
                  >
                    {currentQuestion.actions.map((action) => (
                      <button
                        key={action.value}
                        type="button"
                        style={getActionButtonStyle(action.style)}
                        onClick={() => {
                          chooseAnswer(
                            currentQuestion.id,
                            action.value,
                            action.label,
                          );
                          setScreen(action.nextScreen);
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center" }}>
              {isModern ? (
                <>
                  <div
                    style={{
                      width: 66,
                      height: 66,
                      borderRadius: "50%",
                      margin: "0 auto 14px",
                      display: "grid",
                      placeItems: "center",
                      background: "#E4F8F0",
                    }}
                  >
                    <CheckMark color="#12B981" />
                  </div>
                  <span
                    style={{
                      ...theme.eyebrow,
                      background: "#E4F8F0",
                      color: "#0a7d57",
                    }}
                  >
                    {completion.eyebrow}
                  </span>
                </>
              ) : (
                <span
                  style={{
                    display: "block",
                    color: "var(--br-primary, #667eea)",
                    fontSize: 48,
                    fontWeight: 900,
                    marginBottom: 16,
                  }}
                >
                  ✓
                </span>
              )}
              <h1 style={theme.title}>{completion.title}</h1>
              <p style={theme.subtitle}>{completion.subtitle}</p>
              {cbsCode ? (
                <div
                  style={{
                    marginTop: 18,
                    borderRadius: isModern ? 16 : 12,
                    padding: isModern ? 18 : 20,
                    background: isModern
                      ? "linear-gradient(135deg, #0B1220 0%, #16305e 100%)"
                      : "var(--br-soft, #f0f4ff)",
                    color: isModern ? "#fff" : theme.accent,
                    textAlign: isModern ? "left" : "center",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: 0,
                      textTransform: "uppercase",
                      color: isModern ? "#9fb3d8" : "#666",
                      fontWeight: 700,
                    }}
                  >
                    Your CBS Code
                  </div>
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: 25,
                      fontWeight: 900,
                      letterSpacing: 0,
                      marginTop: 6,
                    }}
                  >
                    {cbsCode}
                  </div>
                  {completion.showCopyButton ? (
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard
                          ?.writeText(cbsCode)
                          .then(() => message.success("CBS code copied."));
                      }}
                      style={{
                        position: "absolute",
                        right: 14,
                        bottom: 14,
                        background: "rgba(255,255,255,.12)",
                        border: "1px solid rgba(255,255,255,.2)",
                        color: "#fff",
                        borderRadius: "var(--br-card-radius, 9px)",
                        padding: "7px 11px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Copy
                    </button>
                  ) : null}
                </div>
              ) : null}
              {completion.helper ? (
                <p
                  style={{
                    fontSize: 13,
                    color: "#666",
                    marginTop: 18,
                    marginBottom: 20,
                    lineHeight: 1.5,
                  }}
                >
                  {completion.helper}
                </p>
              ) : null}
              {completion.trustBadges?.length ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 16,
                    flexWrap: "wrap",
                    marginTop: 18,
                    paddingTop: 16,
                    borderTop: "1px solid #e5e7eb",
                    color: isModern ? "#6B7A90" : "#666",
                    fontSize: isModern ? 11.5 : 12,
                    fontWeight: 600,
                  }}
                >
                  {completion.trustBadges.map((badge) => (
                    <span key={badge}>✓ {badge}</span>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                style={{ ...primaryButtonStyle, marginTop: 22 }}
                onClick={finish}
              >
                {completion.button}
              </button>
              {completion.fine ? (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: 11,
                    color: "#9aa7b8",
                    marginTop: 14,
                    lineHeight: 1.5,
                  }}
                >
                  {completion.fine}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const CbsCampagin = () => {
  const [step, setStep] = useState(1);
  const [cbsIntentVariant] = useState(() =>
    Math.random() < 0.5 ? "classic" : "modern"
  );
  const [cbsIntentModalOpen, setCbsIntentModalOpen] = useState(false);
  const [cbsIntentCompleted, setCbsIntentCompleted] = useState(false);
  const [cbsIntentAnswers, setCbsIntentAnswers] = useState({});
  const [cbsIntentLoaded, setCbsIntentLoaded] = useState(false);
  const cbsCodeShownTrackedRef = useRef(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [freeCbsCode, setFreeCbsCode] = useState("");
  const [fileList] = useState([]);
  const [creditReportFile] = useState([]);
  const [creditFile, setCreditFile] = useState(null);
  const [stepModal, setStepModal] = useState(false);

  const [CBSImpLoan, setCBSImpLoan] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [bankcruptcyProceedings, setBankcruptcyProceedings] = useState(null);
  const [overdueBalance, setOverdueBalance] = useState(null);
  const [monthlyCommitment, setMonthlyCommitment] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [enquiryDate, setEnquiryDate] = useState(null);
  const [chartMaxValue, setChartMaxValue] = useState(null);
  const [
    cBSImpLoanSupplementaryMonthlyInstallment,
    setCBSImpLoanSupplementaryMonthlyInstallment,
  ] = useState(null);

  const [userDetails, setUserDetails] = useState(null);
  const [allCBSData, setAllCBSData] = useState([]);
  const [badgeVerified, setBadgeVerified] = useState(false);
  const [needOtherAuthoriser, setNeedOtherAuthoriser] = useState(false);
  const [otherAuthoriserDetails, setOtherAuthoriserDetails] = useState(null);
  const [liveUserData, setLiveUserData] = useState(null);
  const [keyman, setKeyman] = useState("");
  const [dataSingpassModal, setDataSingpassModal] = useState(false);
  const [singpassData, setSingpassData] = useState(null);
  const [viewingTab, setViewingTab] = useState(0);
  const [combinedSingpassData, setCombinedSingpassData] = useState([]);
  const [openDataIndex, setOpenDataIndex] = useState(null);
  const [eligibleForFundingSocietyLoan, setEligibleForFundingSocietyLoan] =
    useState(false);
  const [visitorToken] = useState(() => {
    const existing = Cookies.get(LANDING_PAGE_VISITOR_COOKIE);
    if (existing) return existing;

    const generated = createVisitorToken();
    Cookies.set(LANDING_PAGE_VISITOR_COOKIE, generated, {
      path: "/",
      expires: 365,
    });
    return generated;
  });

  const history = useHistory();

  const cbsIntentUserId = userDetails?.user_id;
  useEffect(() => {
    if (!cbsIntentUserId) return;
    let cancelled = false;
    setCbsIntentLoaded(false);
    setCbsIntentCompleted(false);
    setCbsIntentAnswers({});
    setCbsIntentModalOpen(false);

    const restoreIntent = async () => {
      let cached = null;
      try {
        cached = JSON.parse(Cookies.get("cbs_intent_latest") || "null");
      } catch (error) {
        // Ignore malformed cookies and check the account's saved response.
      }
      // Unscoped legacy cookies can belong to another account on this browser.
      if (String(cached?.user_id) === String(cbsIntentUserId) && cached?.answers?.completed_at) {
        setCbsIntentAnswers(cached.answers);
        setCbsIntentCompleted(true);
      }
      try {
        const response = await fetch(CBS_INTENT_ENDPOINT, {
          headers: { Authorization: `Bearer ${Cookies.get("token")}`, Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
        const saved = await response.json();
        if (!cancelled && saved.completed) {
          setCbsIntentAnswers(saved.answers || {});
          setCbsIntentCompleted(true);
        }
      } catch (error) {
        console.error("Failed to load CBS questionnaire completion", error);
      } finally {
        if (!cancelled) setCbsIntentLoaded(true);
      }
    };
    restoreIntent();
    return () => { cancelled = true; };
  }, [cbsIntentUserId]);

  const saveCbsCampaignLeadSnapshot = async (leadData = {}) => {
    try {
      await fetch(CBS_CAMPAIGN_LEAD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          visitor_token: visitorToken,
          landing_page_path: CBS_CAMPAIGN_PATH,
          lead_data: {
            source: CBS_CAMPAIGN_PATH,
            user_id: userDetails?.user_id || null,
            variant: cbsIntentVariant,
            questionnaire_answers: cbsIntentAnswers,
            ...leadData,
          },
        }),
      });
    } catch (error) {
      console.error("Failed to save CBS campaign lead", error);
    }
  };

  const handleCbsIntentComplete = (answers) => {
    const payload = {
      user_id: cbsIntentUserId,
      variant: cbsIntentVariant,
      answers,
    };
    fetch(CBS_INTENT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Cookies.get("token")}`,
        Accept: "application/json",
      },
      body: JSON.stringify({ variant: cbsIntentVariant, answers }),
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
    }).catch((error) => {
      console.error("Failed to save CBS questionnaire completion", error);
    });
    Cookies.set("cbs_intent_variant", cbsIntentVariant, {
      path: "/",
      expires: 30,
    });
    Cookies.set("cbs_intent_latest", JSON.stringify(payload), {
      path: "/",
      expires: 30,
    });
    setCbsIntentAnswers(answers || {});

    const selectedProperty = answers?.reason === "property";
    const requestedOptions = [
      answers?.second_opinion,
      answers?.compare_options,
    ].some((answer) => ["yes_second_opinion", "yes_compare"].includes(answer));

    if (selectedProperty) {
      Cookies.set("cbs_intent_home_property_selected", "1", {
        path: "/",
        expires: 30,
      });
      trackCbsAnalyticsEvent("cbs_home_property_intent_selected", {
        variant: cbsIntentVariant,
        answers,
      });
    }

    if (requestedOptions) {
      Cookies.set("cbs_intent_options_requested", "1", {
        path: "/",
        expires: 30,
      });
      trackCbsAnalyticsEvent("cbs_options_interest_selected", {
        variant: cbsIntentVariant,
        answers,
      });
    }

    saveCbsCampaignLeadSnapshot({
      stage: "questionnaire_completed",
      selected_property: selectedProperty,
      requested_options: requestedOptions,
      questionnaire_answers: answers,
    });
    console.log("CBS intent questionnaire completed", payload);
    trackCbsAnalyticsEvent("cbs_questionnaire_completed", {
      variant: cbsIntentVariant,
      answers,
      duration_ms: answers?.duration_ms || null,
    });
    setCbsIntentCompleted(true);
    setCbsIntentModalOpen(false);
  };

  useEffect(() => {
    if (!Cookies.get("tourDoneLender")) {
      setIsTourOpen(true);
      Cookies.set("tourDoneLender", true);
    }
  }, []);

  useEffect(() => {
    if (Cookies.get("signupthroughcbs")) {
      Cookies.remove("signupthroughcbs");
    }
  }, []);

  const tourSteps = [
    {
      selector: ".step1s",
      content:
        "Here is your free CBS Promo Code that you can use to retreive your free CBS Report, you can hit the button on the right to copy it.",
    },
    {
      selector: ".step2s",
      content:
        "If you've already got your CBS Report and you would like to explore your financing options go ahead and click this button.",
    },
    {
      selector: ".step3s",
      content:
        "Once you've copied your CBS Promo Code, you may hit the Get CBS Report highlighted here to be redirected to the CBS website to retreive your free CBS Report.",
    },
  ];

  const availableTab = [
    { name: "Personal Information" },
    { name: "Corporate Information" },
    { name: "NOA History" },
    { name: "Capitals" },
    { name: "Financials" },
    { name: "Shareholders" },
    { name: "Borrower Report" },
  ];

  const getShareholderAddress = (data) => {
    const allSingpassData = [singpassData];
    if (otherAuthoriserDetails !== null && otherAuthoriserDetails.length > 0) {
      for (const single of otherAuthoriserDetails) {
        allSingpassData.push(JSON.parse(single.singpass_data));
      }
      const toPush = allSingpassData.filter(
        (sd) => getPersonData(sd)?.name?.value === data
      );
      // console.log(toPush);
      return toPush;
    } else {
      return allSingpassData;
    }
  };
  const singpassDetails = async (
    user,
    other_authorization,
    loan_id,
    keyman
  ) => {
    try {
      setKeyman(keyman);
      const response = await fetch(
        `https://www.smart-lend.com/api/user/getBorrowerSingppass?user_id=${user}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      const json = await response.json();
      let allSingpassData = [json.singpass_data];
      setDataSingpassModal(true);
      if (other_authorization) {
        console.log("need other authorizer");
        setNeedOtherAuthoriser(true);
        try {
          const response2 = await fetch(
            `https://www.smart-lend.com/api/loanDetails/getLoanAuthorisers`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("token")}`,
              },
              body: JSON.stringify({ loan_id: loan_id }),
            }
          );

          const json2 = await response2.json();
          setOtherAuthoriserDetails(json2.authorisers);
          console.log(JSON.parse(json2.authorisers[0].singpass_data));
          console.log(json.singpass_data);
          setLiveUserData(json.singpass_data);
          console.log(json2.authorisers);

          for (const single of json2.authorisers) {
            allSingpassData.push(JSON.parse(single.singpass_data));
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        console.log("Doesnt need other authoriser");
        setLiveUserData(json.singpass_data);
        setNeedOtherAuthoriser(false);
        setOtherAuthoriserDetails(null);
      }
      if (json.singpass_data) {
        setSingpassData(json.singpass_data);
        console.log(allSingpassData);
        setCombinedSingpassData(allSingpassData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userDetails) {
      setBadgeVerified(userDetails?.badge_verified || 0);

      const dataToPush = [
        {
          name: getPersonData(JSON.parse(userDetails.singpass_data))?.name?.value,
          cbs_data: JSON.parse(userDetails.credit_report_data),
        },
      ];
      console.log(JSON.parse(userDetails?.singpass_data))
      console.log(dataToPush);
      setAllCBSData(dataToPush);
      singpassDetails(userDetails.user_id);
    }
  }, [userDetails]);

  const getUserDetails = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/profile/getUserDetails",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.status === 200) {
        const json = await response.json();
        setUserDetails(json.User);
        if (json.User.credit_report_data) {
          setStep(4);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userDetails) {
      let eligibleForFundingSociety = true;
      const creditReportData = JSON.parse(userDetails.credit_report_data);
      const singpassData = JSON.parse(userDetails.singpass_data);
      // console.log(creditReportData, singpassData);

      const registrationDateString =
        singpassData?.entity?.["basic-profile"]?.["registration-date"]?.value;

      if (registrationDateString) {
        const registrationDate = new Date(registrationDateString);
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

        if (registrationDate < twoYearsAgo) {
          eligibleForFundingSociety = false;
        }
      }

      const companyName =
        singpassData?.entity?.["basic-profile"]?.[
          "entity-name"
        ]?.value?.toLowerCase();
      if (companyName) {
        const hasValidEntityType =
          companyName.includes("pte") || companyName.includes("llp");

        if (!hasValidEntityType) {
          eligibleForFundingSociety = false;
        }
      }

      const individualName = getPersonData(singpassData)?.["name"]?.value;
      const shareholderlist =
        singpassData?.entity?.["shareholders"]?.["shareholders-list"];

      if (shareholderlist && Array.isArray(shareholderlist)) {
        let totalShares = 0;
        let individualShares = 0;
        let singaporeanShares = 0;

        shareholderlist.forEach((shareholder) => {
          const allocation = shareholder.allocation?.value || 0;
          totalShares += allocation;

          // Check if the shareholder matches the individualName
          const personName = shareholder["person-name"]?.value;

          if (personName?.toLowerCase() === individualName?.toLowerCase()) {
            individualShares += allocation;
          }

          // Check if the shareholder is Singaporean
          if (shareholder["person-reference"]?.nationality?.code === "SG") {
            singaporeanShares += allocation;
          }
        });

        // Check individual share ownership (more than 50%)
        if (totalShares > 0 && individualShares / totalShares <= 0.5) {
          // console.log("individual share less than 50%")
          eligibleForFundingSociety = false;
        }

        // Check Singaporean share ownership (more than 30%)
        if (totalShares > 0 && singaporeanShares / totalShares <= 0.3) {
          // console.log("singaporean share less than 30%")
          eligibleForFundingSociety = false;
        }
      }

      const individualDOB = getPersonData(singpassData)?.["dob"]?.value;
      if (individualDOB) {
        const dob = new Date(individualDOB);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear();
        const isBirthdayPassed =
          today.getMonth() > dob.getMonth() ||
          (today.getMonth() === dob.getMonth() &&
            today.getDate() >= dob.getDate());

        const adjustedAge = isBirthdayPassed ? age : age - 1;

        if (adjustedAge >= 50) {
          eligibleForFundingSociety = false;
        }
      }

      const CBSRating = creditReportData?.RiskGrade;
      if (CBSRating === "HH" || CBSRating === "GG") {
        eligibleForFundingSociety = false;
      }

      const overDueBalance = creditReportData?.overdueBalance;
      if (overDueBalance) {
        eligibleForFundingSociety = false;
      }

      const outstandingDefaults = creditReportData?.defaults;
      const bankcruptcyProceedings = creditReportData?.bankcruptcyProceedings;

      if (bankcruptcyProceedings + outstandingDefaults !== 0) {
        eligibleForFundingSociety = false;
      }

      const noaLatest = getPersonData(singpassData)?.["noahistory"]?.noas?.[0];
      if (noaLatest) {
        console.log(noaLatest);
        if (noaLatest.amount.value < 45000) {
          eligibleForFundingSociety = false;
        }
      } else {
        eligibleForFundingSociety = false;
      }
      setEligibleForFundingSocietyLoan(eligibleForFundingSociety);
      // console.log("Eligibility:", eligibleForFundingSociety);
    }
  }, [userDetails]);

  useEffect(() => {}, []);

  useEffect(() => {
    console.log(chartMaxValue);
    if (
      fileList &&
      creditFile !== null &&
      CBSImpLoan !== null &&
      defaults !== null &&
      bankcruptcyProceedings !== null &&
      overdueBalance !== null &&
      monthlyCommitment !== null &&
      chartData !== null &&
      enquiryDate !== null &&
      chartMaxValue !== null &&
      cBSImpLoanSupplementaryMonthlyInstallment &&
      isLessThanOneMonthAgo(enquiryDate)
    ) {
      uploadCreditReportFile();
      setStep(4);
    }
  }, [
    enquiryDate,
    fileList,
    creditFile,
    cBSImpLoanSupplementaryMonthlyInstallment,
    CBSImpLoan,
    defaults,
    bankcruptcyProceedings,
    overdueBalance,
    monthlyCommitment,
    chartData,
    enquiryDate,
    chartMaxValue,
  ]);

  function splitStringToRows(inputString) {
    // Use a regular expression to match exactly two spaces
    const rows = inputString.split(/(?<! ) {2}(?! )/);

    return rows;
  }

  function extractTextBetweeforloandetails(creditText, beforeText, afterText) {
    // Find the starting index of beforeText in the original text
    if (creditText !== null) {
      const startIndex = creditText.indexOf(beforeText);
      if (startIndex === -1) {
        return "Before text not found";
      }

      // Adjust the starting index to exclude the beforeText
      const adjustedStartIndex = startIndex + beforeText.length;

      // Find the ending index of afterText in the original text
      const endIndex = creditText.indexOf(afterText, adjustedStartIndex);
      if (endIndex === -1) {
        return "After text not found";
      }

      // Extract the text between beforeText and afterText
      const extractedText = creditText.substring(adjustedStartIndex, endIndex);

      return extractedText;
    }
  }

  function removeCharactersAroundPage(inputString) {
    // Regular expression to find the word "page" and capture the preceding and following characters
    const regex = /(.{0,19})page(.{0,7})/g; // Adjusted to capture more characters before "page"

    // Replace the matched segments with the word "page" alone
    const result = inputString.replace(regex, (match, p1, p2) => {
      // Determine the number of characters to remove from the beginning and the end
      const charsToRemoveBefore = 19 - 11; // 4 characters before the original 11
      const charsToRemoveAfter = 11 - 4;

      const removedStart =
        p1.length > charsToRemoveBefore
          ? p1.slice(0, -charsToRemoveBefore)
          : "";
      const removedEnd =
        p2.length > charsToRemoveAfter ? p2.slice(charsToRemoveAfter) : "";
      return `${removedStart}page${removedEnd}`;
    });

    return result;
  }

  const uploadCreditReportFile = async () => {
    try {
      const formData1 = new FormData();
      formData1.append("credit_report", creditReportFile);
      const creditReportData = {
        EnquiryDate:
          creditFile && creditFile !== "" && enquiryDate ? enquiryDate : null,
        SecuredCreditLimit:
          creditFile && creditFile !== ""
            ? parseFloat(
                extractTextBetween(
                  "Secured Credit Limit",
                  "Unsecured Credit Limit"
                ).replace(/,/g, "")
              )
            : null,
        UnsecuredCreditLimit:
          creditFile && creditFile !== ""
            ? parseFloat(
                extractTextBetween(
                  "Unsecured Credit Limit",
                  "Exempted Credit Limit"
                ).replace(/,/g, "")
              )
            : null,
        CreditScore:
          creditFile && creditFile !== ""
            ? parseFloat(
                extractTextBetween(
                  "financial institution.  Score",
                  "Risk Grade"
                )
              )
            : null,
        RiskGrade:
          creditFile && creditFile !== ""
            ? extractTextBetween("Risk Grade ", "Risk Grade Description")
            : null,
        ProbabilityofDefault:
          creditFile && creditFile !== ""
            ? parseFloat(
                extractTextBetween(
                  "Probability of Default ",
                  " For more details on the description"
                ).replace(/,/g, "")
              )
            : null,
        SecuredCreditUtilized:
          creditFile && creditFile !== ""
            ? extractTextBetween(
                "Total  ",
                " Aggregated Outstanding Balances for Preceding 5 Months"
              )
                ?.split("  ")
                ?.map((num) => parseFloat(num.replace(/,/g, "")))[0]
            : null,
        UnsecuredCreditUtilized:
          creditFile && creditFile !== ""
            ? extractTextBetween(
                "Total  ",
                " Aggregated Outstanding Balances for Preceding 5 Months"
              )
                ?.split("  ")
                ?.map((num) => parseFloat(num.replace(/,/g, "")))[1]
            : null,
        SecuredCreditUtilizationinpercentage:
          creditFile && creditFile !== ""
            ? (
                (extractTextBetween(
                  "Total  ",
                  " Aggregated Outstanding Balances for Preceding 5 Months"
                )
                  ?.split("  ")
                  ?.map((num) => parseFloat(num.replace(/,/g, "")))[0] /
                  parseFloat(
                    extractTextBetween(
                      "Secured Credit Limit",
                      "Unsecured Credit Limit"
                    )?.replace(/,/g, "")
                  )) *
                100
              )?.toFixed(2)
            : null,
        UnsecuredCreditUtilizationinpercentage:
          creditFile && creditFile !== ""
            ? (
                (extractTextBetween(
                  "Total  ",
                  " Aggregated Outstanding Balances for Preceding 5 Months"
                )
                  ?.split("  ")
                  ?.map((num) => parseFloat(num.replace(/,/g, "")))[1] /
                  parseFloat(
                    extractTextBetween(
                      "Unsecured Credit Limit",
                      "Exempted Credit Limit"
                    )?.replace(/,/g, "")
                  )) *
                100
              )?.toFixed(2)
            : null,
        MonthlyCommitment:
          creditFile && creditFile !== "" && monthlyCommitment
            ? monthlyCommitment.toLocaleString("en-US", {
                style: "currency",
                currency: "SGD",
              })
            : null,
        NoteworthyOngoingLoans:
          CBSImpLoan && CBSImpLoan.length !== 0 ? CBSImpLoan : null,
        Last6MonthsGraphChartMaxData:
          chartMaxValue && chartData ? chartMaxValue : null,
        Last6MonthsGraphChartData:
          chartMaxValue && chartData ? chartData : null,
        CBSImpLoanSupplementaryInstallment:
          cBSImpLoanSupplementaryMonthlyInstallment
            ? cBSImpLoanSupplementaryMonthlyInstallment
            : null,
        defaults: defaults,
        bankcruptcyProceedings: bankcruptcyProceedings,
        overdueBalance: overdueBalance,
      };
      formData1.append("credit_report_data", JSON.stringify(creditReportData));

      const updateBorrowerCreditReport = await fetch(
        "https://www.smart-lend.com/api/user/borrowerUpdateCreditReport",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: formData1,
        }
      );

      const json = await updateBorrowerCreditReport.json();
      console.log(json);
      if (updateBorrowerCreditReport.ok) {
        getUserDetails();
      }
    } catch (error) {
      console.error(error);
    }
  };

  function convertDate(dateString) {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Split the input date string
    const [day, month, year] = dateString.split("/");

    // Convert month number to month name
    const monthName = months[parseInt(month) - 1];

    // Return the formatted date string
    return `${day} ${monthName} ${year}`;
  }
  const isLessThanOneMonthAgo = (dateString) => {
    // Parse the input date string (assuming format is "DD/MM/YYYY")
    const [day, month, year] = dateString.split("/").map(Number);
    const inputDate = new Date(year, month - 1, day); // JavaScript months are 0-based

    // Get the current date
    const currentDate = new Date();

    // Calculate the difference in months and years
    const yearDiff = currentDate.getFullYear() - inputDate.getFullYear();
    const monthDiff = currentDate.getMonth() - inputDate.getMonth();

    // Check if the input date is within the last month
    if (yearDiff === 0 && monthDiff <= 1) {
      if (monthDiff === 1) {
        // If the difference is exactly 1 month, check the day to determine if it's within 30/31 days
        const daysDiff = currentDate.getDate() - inputDate.getDate();
        return daysDiff <= 0;
      }
      return true;
    } else if (
      yearDiff === 1 &&
      currentDate.getMonth() === 0 &&
      inputDate.getMonth() === 11
    ) {
      // Special case for dates in December of the previous year and January of the current year
      return currentDate.getDate() <= inputDate.getDate();
    }

    return false;
  };
  function extractTextBetween(beforeText, afterText) {
    if (creditFile !== null) {
      // Replace multiple spaces in beforeText and afterText with \s+ to match any number of spaces
      const beforeTextPattern = beforeText.replace(/\s+/g, "\\s+");
      const afterTextPattern = afterText.replace(/\s+/g, "\\s+");

      // Create a regex to find text between the beforeTextPattern and afterTextPattern
      const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`);

      const match = creditFile.match(regex);
      if (match && match[1]) {
        return match[1].trim(); // Extract and trim any leading/trailing spaces
      } else {
        return "Text not found between specified markers";
      }
    }
  }

  function extractText(file) {
    pdfToText(file)
      .then((text) => {
        console.log(text);
        setCreditFile(text);
      })
      .catch((error) => {
        console.error("Failed to extract text from pdf");
      });
  }
  const UniversaleUploadFile = async (file) => {
    trackCbsAnalyticsEvent("cbs_report_upload_started", {
      variant: cbsIntentVariant,
      file_type: file?.type || null,
      file_size: file?.size || null,
    });
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "CBS");
    formData.append("file_id", "CBS Campaign");
    // console.log(file);
    try {
      const response1 = await fetch(
        "https://www.smart-lend.com/api/file/uploadUniversalFile",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: formData,
        }
      );
      if (response1.status === 200) {
        const data = await response1.json();
        const blobUrl = data.url; // Assuming this is the URL you want to store
        trackCbsAnalyticsEvent("cbs_report_upload_completed", {
          variant: cbsIntentVariant,
          file_type: file?.type || null,
          file_size: file?.size || null,
        });
        // console.log(blobUrl);
        return blobUrl;
      }
      trackCbsAnalyticsEvent("cbs_report_upload_failed", {
        variant: cbsIntentVariant,
        status: response1.status,
      });
    } catch (error) {
      trackCbsAnalyticsEvent("cbs_report_upload_failed", {
        variant: cbsIntentVariant,
        error_message: error?.message || "Unknown upload error",
      });
      console.error(error);
    }
  };

  const fetchSingpassData = async () => {
    let userInformation = null;
    let hasSyncedIdentityData = false;
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/profile/getUserDetails",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.status === 200) {
        const json = await response.json();
        setUserDetails(json.User);
        hasSyncedIdentityData = Boolean(
          json.User?.singpass_data || json.User?.myinfo_data
        );
        if (json.User.credit_report_data) {
          userInformation = json.User;
          setStep(4);
        }
      }
    } catch (error) {
      console.error(error);
    }

    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/singpass/getSingpassData",
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        // Handle HTTP error responses (e.g., 404, 500)
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const json = await response.json();
      console.log(json);
      const hasSyncedViaSingpass = json.singpass_data !== null;
      if (!hasSyncedIdentityData && !hasSyncedViaSingpass) {
        setStep(1);
      } else if (userInformation?.credit_report_data) {
        setStep(4);
      } else {
        setStep(2);
        setIsTourOpen(true);
        Cookies.set("tourDoneLender", true);
      }

      // Compare dates
    } catch (error) {
      console.error(error);
    }
  };

  const getCbsCode = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/campaign/getcbscode",
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        // Handle HTTP error responses (e.g., 404, 500)
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const json = await response.json();
      console.log(json);
      if (json.cbs_campaign_code) {
        setFreeCbsCode(json.cbs_campaign_code);
        trackCbsAnalyticsEvent("cbs_code_received", {
          variant: cbsIntentVariant,
          questionnaire_completed: cbsIntentCompleted,
          cbs_code_present: true,
        });
      }
      // Compare dates
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (step === 2) {
      getCbsCode();
    }
  }, [step]);

  useEffect(() => {
    setCbsIntentModalOpen(step === 2 && cbsIntentLoaded && !cbsIntentCompleted);
  }, [cbsIntentCompleted, cbsIntentLoaded, step]);

  useEffect(() => {
    if (step !== 2 || !freeCbsCode || !cbsIntentCompleted) return;
    if (cbsCodeShownTrackedRef.current) return;

    cbsCodeShownTrackedRef.current = true;
    trackCbsAnalyticsEvent("cbs_code_shown", {
      variant: cbsIntentVariant,
      cbs_code_present: true,
    });
  }, [cbsIntentCompleted, cbsIntentVariant, freeCbsCode, step]);

  useEffect(() => {
    fetchSingpassData();
  }, []);

  const shouldShowCbsStepContent =
    step !== 4 && (step !== 2 || cbsIntentCompleted);
  const shouldShowCbsTour =
    isTourOpen && !cbsIntentModalOpen && (step !== 2 || cbsIntentCompleted);

  const triggerSingpassSync = () => {
    console.log(Cookies.get("sgpasscbs"));
    Cookies.set("sgpasscbs", 1);
    handleSyncWithSingpass();
  };

  const handleSyncWithMyinfo = async () => {
    try {
      const setTokenCookieForRedirect = () => {
        const token = Cookies.get("token");
        if (!token) return;
        const options = { expires: 7, path: "/" };
        const hostname = window.location.hostname;
        if (hostname.endsWith("smart-lend.com")) {
          options.domain = ".smart-lend.com";
          options.sameSite = "None";
          options.secure = true;
        }
        Cookies.set("token", token, options);
      };

      setTokenCookieForRedirect();
      const response = await fetch(`https://www.smart-lend.com/api/generateCodeChallenge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify({
          type: "borrower",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const json = await response.json();
      const url = json.singpass_url;
      Cookies.set("latest_session_id", json.session_id);

      if (url?.startsWith("http")) {
        window.location.href = url;
      }
    } catch (err) {
      console.error("Error fetching MyInfo URL:", err);
      message.error("Unable to start MyInfo sync. Please try again.");
    }
  };

  const handleSyncWithSingpass = async () => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/singpass/getAuthoriseApi`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      // Read the response as text since it's not JSON
      const url = await response.text();
      console.log("URL value: ", url);

      if (url.startsWith("http")) {
        // Simple check to ensure it looks like a URL
        window.location.href = url;
      }
    } catch (err) {
      console.error("Error fetching Singpass URL:", err);
    }
  };

  useEffect(() => {
    if (creditFile) {
      const textWithoutPage = removeCharactersAroundPage(
        creditFile.toLowerCase()
      ).replace(/page/g, "");

      const extract = extractTextBetweeforloandetails(
        textWithoutPage,
        "aggregated outstanding balances  month   product type   grantor bank   secured balances  unsecured balances interest bearing  unsecured balances non-interest bearing  exempted unsecured balances  ",
        "total"
      );

      const extract2ForMonthlyCommitment = extractTextBetweeforloandetails(
        textWithoutPage,
        "aggregated monthly instalment",
        "aggregated monthly instalments for preceding 5 months"
      );

      console.log(extract2ForMonthlyCommitment);

      const extract3ForChartingOutstandingBalance =
        extractTextBetweeforloandetails(
          creditFile,
          "Aggregated Outstanding Balances for Preceding 5 Months",
          "Aggregated Monthly Instalment"
        );

      const extract4ForChartingOutstandingBalance =
        extractTextBetweeforloandetails(
          textWithoutPage,
          "aggregated monthly instalment  month   product type   grantor bank   account type**   property***   non-property   unsecured  exempted unsecured  ",
          "aggregated monthly instalments for preceding 5 months"
        );

      const extractEnquiryDate = extractTextBetweeforloandetails(
        textWithoutPage,
        "enquiry date",
        "reference"
      );

      if (extractEnquiryDate !== "Before text not found") {
        setEnquiryDate(extractEnquiryDate)?.trim();
      }
      function parseData(inputString) {
        // Adjusted regex to account for extra numbers before the relevant value
        const regex =
          /([a-zA-Z]+\s\d{4})\s+All\s+All\s+[\d,.]+\s+([\d,.]+\.\d{2})/g;

        const result = [];
        let match;

        // Loop through the matches and create the array of objects
        while ((match = regex.exec(inputString)) !== null) {
          const date = match[1];
          const value = match[2];
          result.push({ date, value });
        }

        return result;
      }

      function getMaxValue(data) {
        return data.reduce((max, current) => {
          // Parse the current value to a float for accurate comparison
          const currentValue = parseFloat(current.value.replace(/,/g, ""));
          return currentValue > max ? currentValue : max;
        }, 0);
      }
      function getNextMonth(data) {
        const monthNames = [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ];

        // Extract the first date from the data
        const firstDate = data?.[0]?.date;
        if (firstDate !== undefined) {
          const [firstMonth, firstYear] = firstDate.split(" ");
          const monthIndex = monthNames.indexOf(firstMonth.toLowerCase());
          const year = parseInt(firstYear, 10);

          // Calculate the next month and year
          const nextMonthIndex = (monthIndex + 1) % 12;
          const nextYear = monthIndex === 11 ? year + 1 : year;
          const nextMonth = monthNames[nextMonthIndex];

          return `${nextMonth} ${nextYear}`;
        }
      }
      let chartDate = parseData(extract3ForChartingOutstandingBalance);
      // console.log(chartDate);
      if (chartDate && chartDate.length > 0) {
        chartDate.unshift({
          date: getNextMonth(parseData(extract3ForChartingOutstandingBalance)),
          value: extractTextBetween(
            "Total  ",
            " Aggregated Outstanding Balances for Preceding 5 Months"
          )
            ?.split("   ")[1]
            .trim(),
        });

        setChartData(chartDate.reverse());
        setChartMaxValue(getMaxValue(chartDate));
      }
      // console.log(parseData(extract3ForChartingOutstandingBalance));

      // console.log(
      //   splitStringToRows(extract).filter(
      //     (row) =>
      //       row.includes("private residential purchase") ||
      //       row.includes("motor vehicle loan") ||
      //       row.includes("hdb loan")
      //   )
      // );

      // console.log(
      //   splitStringToRows(extract).filter(
      //     (row) =>
      //       row.includes("private residential purchase") ||
      //       row.includes("motor vehicle loan") ||
      //       row.includes("hdb loan")
      //   )
      // );
      const splitExactlyTwoSpaces = (inputString) => {
        // Split the string using the regular expression for exactly two spaces
        const splitArray = inputString.split(/(?<! )  (?! )/);
        return splitArray;
      };
      const combineSingleOrJoint = (splitArray) => {
        const combinedArray = [];

        for (let i = 0; i < splitArray.length; i++) {
          if (
            splitArray[i].startsWith("single") ||
            splitArray[i].startsWith("joint")
          ) {
            if (combinedArray.length > 0) {
              combinedArray[combinedArray.length - 1] += "  " + splitArray[i];
            } else {
              combinedArray.push(splitArray[i]);
            }
          } else {
            combinedArray.push(splitArray[i]);
          }
        }

        return combinedArray;
      };

      const combinePrivateResidentialPurchase = (splitArray) => {
        const combinedArray = [];

        for (let i = 0; i < splitArray.length; i++) {
          if (splitArray[i].includes("private residential purchase")) {
            combinedArray.push(
              splitArray[i] + "  " + (splitArray[i + 1] || "")
            );
            i++; // Skip the next element as it has been combined
          } else {
            combinedArray.push(splitArray[i]);
          }
        }

        return combinedArray;
      };
      // console.log(
      //   combineSingleOrJoint(
      //     combinePrivateResidentialPurchase(
      //       splitExactlyTwoSpaces(extract4ForChartingOutstandingBalance)
      //     )
      //   ).filter(
      //     (row) =>
      //       row.includes("private residential purchase") ||
      //       row.includes("motor vehicle loan") ||
      //       row.includes("hdb loan")
      //   )
      // );

      setCBSImpLoanSupplementaryMonthlyInstallment(
        combineSingleOrJoint(
          combinePrivateResidentialPurchase(
            splitExactlyTwoSpaces(extract4ForChartingOutstandingBalance)
          )
        ).filter(
          (row) =>
            row.includes("private residential purchase") ||
            row.includes("motor vehicle loan") ||
            row.includes("hdb loan")
        )
      );

      setMonthlyCommitment(
        parseFloat(
          extract2ForMonthlyCommitment
            .trim()
            .split("   ")
            [
              extract2ForMonthlyCommitment.trim().split("   ").length - 4
            ]?.replace(/,/g, "")
        ) +
          parseFloat(
            extract2ForMonthlyCommitment
              .trim()
              .split("   ")
              [
                extract2ForMonthlyCommitment.trim().split("   ").length - 3
              ]?.replace(/,/g, "")
          )
      );

      setCBSImpLoan(
        splitStringToRows(extract).filter(
          (row) =>
            row.includes("private residential purchase") ||
            row.includes("motor vehicle loan") ||
            row.includes("hdb loan")
        )
      );
      console.log(
        parseFloat(
          extractTextBetween("Defaults   ", "  Bankruptcy Proceedings   ")
        ),
        parseFloat(
          extractTextBetween(
            "  Bankruptcy Proceedings   ",
            "  Secured Credit Limit "
          )
        )
      );
      setDefaults(
        parseFloat(
          extractTextBetween("Defaults   ", "  Bankruptcy Proceedings   ")
        )
      );
      setBankcruptcyProceedings(
        parseFloat(
          extractTextBetween(
            "  Bankruptcy Proceedings   ",
            "  Secured Credit Limit "
          )
        )
      );
      const targetLine =
        "  account status history product type   grantor bank   account type date opened / date closed   overdue balance last 12 cycles / cash advance / balance transfer full payment  ";

      function formatAndMergeString(input) {
        // Define patterns to split by
        const patterns = [
          "unsecured overdraft",
          "unsecured credit card",
          "other",
          "motor vehicle loan",
          "hdb loan",
          "private residential purchase",
          "unsecured personal loan",
        ];

        // Create a regular expression to split the string at the start of each record
        const regex = new RegExp(`(${patterns.join("|")})`, "g");

        // Split and rejoin the string with line breaks
        const formattedString = input
          .split(regex) // Split using the pattern
          .filter(Boolean) // Remove empty strings
          .map((str) => str.trim()) // Trim unnecessary spaces
          .join("\n"); // Rejoin with line breaks

        // Now, split each line by three or more spaces to create arrays
        const lines = formattedString.split("\n");

        // Merge consecutive elements, but check if the next element exists
        const result = [];
        for (let i = 0; i < lines.length; i += 2) {
          // Check if the next element exists before attempting to split
          if (lines[i + 1]) {
            const record = [lines[i]].concat(
              lines[i + 1].split(/\s{3,}/).map((part) => part.trim())
            );
            result.push(record);
          } else {
            // If there is no next element, just push the current one as is
            result.push([lines[i]]);
          }
        }

        return result;
      }

      function hasOverdueBalance(data) {
        // Iterate over the data (array of arrays)
        return data.some((item) => {
          // Check if the fifth index exists and is a valid number (i.e., a string that can be converted to a number)
          const fifthIndex = item[4];
          return !isNaN(fifthIndex) && parseFloat(fifthIndex) > 0; // If it's a number and greater than 0, consider it as overdue balance
        });
      }

      const extract8ForOverdueBalance = extractTextBetweeforloandetails(
        textWithoutPage,
        "account status history",
        "previous enquiries  date "
      )
        .replace(new RegExp(targetLine, "g"), " ") // Replace all occurrences of targetLine
        .replace(/account status history/g, "") // Replace all occurrences of the specific phrases
        .replace(/product type/g, "")
        .replace(/grantor bank/g, "")
        .replace(/account type/g, "")
        .replace(/date opened \/ date closed/g, "")
        .replace(/overdue balance/g, "")
        .replace(
          /last 12 cycles \/ cash advance \/ balance transfer full payment/g,
          ""
        )
        .trim(); // Trim any leading/trailing spaces

      console.log(
        hasOverdueBalance(formatAndMergeString(extract8ForOverdueBalance))
      );
      setOverdueBalance(
        hasOverdueBalance(formatAndMergeString(extract8ForOverdueBalance))
      );
    }
  }, [creditFile]);

  const hasPropertyFinancingIntent = cbsIntentAnswers?.reason === "property";

  const getRequestedOptionsIntent = useCallback(
    () =>
      [
        cbsIntentAnswers?.second_opinion,
        cbsIntentAnswers?.compare_options,
      ].some((answer) => ["yes_second_opinion", "yes_compare"].includes(answer)),
    [cbsIntentAnswers]
  );

  const handlePostCbsQuestionnaireStart = useCallback(
    (metadata) => {
      trackCbsAnalyticsEvent("cbs_post_questionnaire_started", {
        variant: cbsIntentVariant,
        selected_property: hasPropertyFinancingIntent,
        requested_options: getRequestedOptionsIntent(),
        ...metadata,
      });
    },
    [cbsIntentVariant, getRequestedOptionsIntent, hasPropertyFinancingIntent]
  );

  const handlePostCbsQuestionnaireLeave = useCallback(
    (metadata) => {
      trackCbsAnalyticsEvent("cbs_post_questionnaire_left", {
        variant: cbsIntentVariant,
        selected_property: hasPropertyFinancingIntent,
        requested_options: getRequestedOptionsIntent(),
        ...metadata,
      });
    },
    [cbsIntentVariant, getRequestedOptionsIntent, hasPropertyFinancingIntent]
  );

  const handlePostCbsLoanSubmit = async (leadData) => {
    const requestedOptions = [
      cbsIntentAnswers?.second_opinion,
      cbsIntentAnswers?.compare_options,
    ].some((answer) => ["yes_second_opinion", "yes_compare"].includes(answer));
    const {
      duration_ms,
      questionnaire_answers,
      type,
      ...inputPayload
    } = leadData;

    try {
      const response = await fetch(CBS_POST_QUESTIONNAIRE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify({
          visitor_token:
            safeStorageGet(window.localStorage, "smartlend_analytics_visitor_id") ||
            visitorToken,
          session_id: safeStorageGet(
            window.sessionStorage,
            "smartlend_analytics_session_id"
          ),
          variant: cbsIntentVariant,
          form_type: type,
          selected_property: hasPropertyFinancingIntent,
          requested_options: requestedOptions,
          duration_ms: duration_ms || null,
          questionnaire_answers: questionnaire_answers || cbsIntentAnswers,
          input_payload: inputPayload,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || "Unable to submit request.");
      }

      trackCbsAnalyticsEvent("cbs_post_questionnaire_submitted", {
        variant: cbsIntentVariant,
        form_type: type,
        duration_ms: duration_ms || null,
        selected_property: hasPropertyFinancingIntent,
        requested_options: requestedOptions,
        ...inputPayload,
      });
      trackCbsAnalyticsEvent("cbs_post_report_loan_request_submitted", {
        variant: cbsIntentVariant,
        selected_property: hasPropertyFinancingIntent,
        requested_options: requestedOptions,
        ...leadData,
      });
      saveCbsCampaignLeadSnapshot({
        stage: "post_cbs_request_submitted",
        selected_property: hasPropertyFinancingIntent,
        requested_options: requestedOptions,
        ...leadData,
      });
      message.success("Thanks. Our consultants will reach out within 24 hours.");
      return true;
    } catch (error) {
      console.error("Failed to submit CBS post questionnaire", error);
      message.error(error.message || "Unable to submit request. Please try again.");
      return false;
    }
  };

  return (
    <div
      style={{
        height:
          window.innerWidth < 1200
            ? `calc(${window.innerHeight}px - 80px)`
            : "calc(100% - 80px)",
        width: window.innerWidth < 1200 ? "100%" : "calc(100% - 40px)",
        marginLeft: window.innerWidth < 1200 ? "0px" : "20px",
        background:
          "var(--br-bg, #f5f6ff)",
        padding: window.innerWidth < 1200 ? "18px" : "28px",
        borderRadius: "var(--br-card-radius, 24px)",
        border: "1px solid #E7ECF3",
        boxShadow: "var(--br-shadow, 0 24px 70px -35px rgba(13, 38, 90, 0.42))",
        marginTop: "5px",
        gap: "5px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: step !== 4 ? "" : "flex-start",
        justifyContent: step !== 4 ? "" : "flex-start",
        overflowY: "auto",
      }}
    >
      <Modal
        footer={null}
        open={stepModal !== false}
        // onOk={handleOk}
        onCancel={() => setStepModal(false)}
      >
        {stepModal === 1 ? (
          <div>
            <img
              style={{
                width: "80%",
                height: "80%",
                objectFit: "cover",
                borderRadius: "var(--br-card-radius, 10px)",
                boxShadow: "var(--br-shadow, 0px 0px 10px rgba(0,0,0,0.1))",
                marginTop: 15,
              }}
              src={singpassmobilepage}
            />
            <div style={{ fontWeight: 700, fontSize: 20, marginTop: 20 }}>
              Sync your account with Singpass
            </div>
            <div style={{ fontWeight: 300, fontSize: 14, marginTop: 10 }}>
              Click on Sync Singpass located at the bottom of the page where
              you'll be brought to Singpass official website to allow our
              platform to confirm your identity.
            </div>
          </div>
        ) : stepModal === 2 ? (
          <div>
            <img
              style={{
                width: "80%",
                height: "80%",
                objectFit: "cover",
                borderRadius: "var(--br-card-radius, 10px)",
                boxShadow: "var(--br-shadow, 0px 0px 10px rgba(0,0,0,0.1))",
                marginTop: 15,
              }}
              src={cbsScreenshot}
            />
            <div style={{ fontWeight: 700, fontSize: 20, marginTop: 20 }}>
              Retrieve your Free CBS Report
            </div>
            <div style={{ fontWeight: 300, fontSize: 14, marginTop: 10 }}>
              Copy the promo code above the "get CBS Report" button and click on
              the button where you'll be brought to CBS website where you can
              use the code to redeem your free CBS Report. Proceed to hit Save
              on the final page where you can later upload it back on our
              platform.
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontWeight: 700, fontSize: 20, marginTop: 20 }}>
              Explore your next financing step
            </div>
            <div style={{ fontWeight: 300, fontSize: 14, marginTop: 10 }}>
              After retrieving your report, SmartLend will show a tailored
              refinancing simulator or a quick loan request form based on what
              you selected earlier.
            </div>
          </div>
        )}
      </Modal>
      <CbsIntentQuestionnaireModal
        open={cbsIntentModalOpen}
        variant={cbsIntentVariant}
        cbsCode={freeCbsCode}
        onComplete={handleCbsIntentComplete}
        onAnalyticsEvent={trackCbsAnalyticsEvent}
      />
      {shouldShowCbsStepContent ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 18px",
              borderRadius: "var(--br-card-radius, 18px)",
              background: "var(--br-surface, #FFFFFF)",
              border: "1px solid #E7ECF3",
              boxShadow: "var(--br-shadow, 0 18px 45px -32px rgba(13, 38, 90, 0.5))",
              marginTop: window.innerWidth > 1200 ? "18px" : "4px",
            }}
          >
            <img
              style={{
                width: window.innerWidth > 1200 ? "260px" : "62vw",
                maxWidth: "320px",
                objectFit: "contain",
                height: window.innerWidth > 1200 ? "82px" : "76px",
              }}
              src={cbsImage}
            />
          </div>
          {step !== 3 ? (
            <>
              <div
                style={{
                  color: "#0B1220",
                  fontSize: window.innerWidth > 1200 ? "32px" : "24px",
                  lineHeight: window.innerWidth > 1200 ? "38px" : "30px",
                  fontFamily: "Inter, Manrope, sans-serif",
                  fontWeight: 900,
                  letterSpacing: "0px",
                  marginTop: "22px",
                  marginBottom: "12px",
                  width: window.innerWidth > 1200 ? "680px" : "94%",
                  textAlign: "center",
                }}
              >
                Get Your Free Credit Bureau Report & Unlock Better Business Loan
                Opportunities
              </div>
              <div
                style={{
                  color: "#334155",
                  fontSize: "14px",
                  lineHeight: "22px",
                  fontFamily: "Inter, Manrope, sans-serif",
                  fontWeight: 500,
                  marginBottom: "10px",
                  width: window.innerWidth > 1200 ? "720px" : "92%",
                  textAlign: "center",
                }}
              >
                For a limited time, SmartLend offers you the chance to retrieve
                your official CBS Credit Report at no cost—complete with an
                in-depth analysis to uncover your best loan options.
              </div>
            </>
          ) : null}
          {step !== 3 ? (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: window.innerWidth > 1200 ? "18px" : "12px",
                color: "#0B1220",
                fontSize: "18px",
                fontFamily: "Inter, Manrope, sans-serif",
                fontWeight: 800,
                textAlign: "center",
                marginTop: "20px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <div
                style={{
                  width: window.innerWidth < 1200 ? "calc(50% - 8px)" : "29%",
                  minWidth: window.innerWidth < 1200 ? "150px" : "220px",
                  background: "var(--br-surface, #FFFFFF)",
                  border: "1px solid #E7ECF3",
                  borderRadius: "var(--br-card-radius, 20px)",
                  padding: "14px",
                  boxShadow: "var(--br-shadow, 0 18px 45px -34px rgba(13, 38, 90, 0.55))",
                }}
              >
                <div
                  className={step === 1 ? "blinking-outline" : ""}
                  style={{
                    padding: "3px",
                    borderRadius: "12px",
                  }}
                >
                  <img
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      height: "auto",
                      borderRadius: "var(--br-card-radius, 16px)",
                      background: "var(--br-soft, #F5F8FD)",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setStepModal(1)}
                    src={singpassmobilepage}
                  />
                </div>
                <div style={{ marginTop: "15px" }}>Step 1</div>
                <div style={{ fontSize: "13px" }}>Sync with SingPass</div>
                <div style={{ fontSize: "12px", fontWeight: 400 }}>
                  Get your free CBS access code instantly by scanning your
                  Singpass. (Note: You must have an active business entity
                  registered in Singapore.)
                </div>
              </div>
              <div
                style={{
                  width: window.innerWidth < 1200 ? "calc(50% - 8px)" : "29%",
                  minWidth: window.innerWidth < 1200 ? "150px" : "220px",
                  background: "var(--br-surface, #FFFFFF)",
                  border: "1px solid #E7ECF3",
                  borderRadius: "var(--br-card-radius, 20px)",
                  padding: "14px",
                  boxShadow: "var(--br-shadow, 0 18px 45px -34px rgba(13, 38, 90, 0.55))",
                }}
              >
                <div
                  className={step === 2 ? "blinking-outline" : ""}
                  style={{
                    padding: "3px",
                    borderRadius: "12px",
                  }}
                >
                  <img
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      height: "auto",
                      borderRadius: "var(--br-card-radius, 16px)",
                      background: "var(--br-soft, #F5F8FD)",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setStepModal(2)}
                    src={cbsScreenshot}
                  />
                </div>
                <div style={{ marginTop: "15px" }}>Step 2</div>
                <div style={{ fontSize: "13px" }}>Retrieve Credit Report</div>
                <div style={{ fontSize: "12px", fontWeight: 400 }}>
                  Easily access your official credit report using the [Save]
                  button—avoid using the "Print as PDF" option for a seamless
                  upload to our platform.
                </div>
              </div>
              <div
                style={{
                  width: window.innerWidth < 1200 ? "100%" : "29%",
                  minWidth: window.innerWidth < 1200 ? "150px" : "220px",
                  background: "var(--br-surface, #FFFFFF)",
                  border: "1px solid #E7ECF3",
                  borderRadius: "var(--br-card-radius, 20px)",
                  padding: "14px",
                  boxShadow: "var(--br-shadow, 0 18px 45px -34px rgba(13, 38, 90, 0.55))",
                }}
              >
                <div
                  className={step === 3 ? "blinking-outline" : ""}
                  style={{
                    padding: "3px",
                    borderRadius: "12px",
                  }}
                >
                  <img
                    style={{
                      width: window.innerWidth > 1200 ? "100%" : "58%",
                      aspectRatio: "1 / 1",
                      height: "auto",
                      borderRadius: "var(--br-card-radius, 16px)",
                      background: "var(--br-soft, #F5F8FD)",
                      objectFit: "contain",
                      cursor: "pointer",
                      padding: "18px",
                    }}
                    onClick={() => setStepModal(3)}
                    src={cbsImage}
                    alt="SmartLend financing options"
                  />
                </div>
                <div style={{ marginTop: "15px" }}>Step 3</div>
                <div style={{ fontSize: "13px" }}>Review Financing Options</div>
                <div style={{ fontSize: "12px", fontWeight: 400 }}>
                  After retrieving your report, continue with a refinancing
                  savings simulator or a short financing request so our
                  consultants can follow up with suitable options.
                </div>
              </div>
            </div>
          ) : null}

          {step === 2 && freeCbsCode ? (
            <div
              style={{
                marginTop: 24,
                display: "flex",
                alignItems: "stretch",
                borderRadius: "var(--br-card-radius, 18px)",
                overflow: "hidden",
                background: "linear-gradient(135deg, #0B1220 0%, #16305e 100%)",
                color: "#fff",
                boxShadow: "var(--br-shadow, 0 20px 40px -28px rgba(13, 38, 90, 0.65))",
              }}
              className="step1s"
            >
              <div
                style={{
                  padding: "16px 44px 16px 18px",
                  color: "#fff",
                  fontWeight: "900",
                  fontFamily: "monospace",
                  fontSize: "24px",
                  letterSpacing: "0px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    color: "#9fb3d8",
                    fontSize: "10px",
                    letterSpacing: "0px",
                    textTransform: "uppercase",
                    fontFamily: "Inter, Manrope, sans-serif",
                    marginBottom: "4px",
                  }}
                >
                  Your CBS code
                </div>
                {freeCbsCode}
              </div>
              <div
                onClick={() => {
                  const textToCopy = freeCbsCode; // Replace with the text you want to copy
                  navigator.clipboard
                    .writeText(textToCopy)
                    .then(() => {
                      trackCbsAnalyticsEvent("cbs_code_copied", {
                        variant: cbsIntentVariant,
                      });
                      message.success("Promo code copied to clipboard!");
                    })
                    .catch((err) => {
                      console.error("Failed to copy text: ", err);
                    });
                }}
                style={{
                  background: "rgba(255,255,255,0.13)",
                  color: "white",
                  borderLeft: "1px solid rgba(255,255,255,0.2)",
                  padding: "0 22px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <ContentPasteIcon style={{ color: "white", fontSize: 24 }} />
              </div>
            </div>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "25px",
            }}
          >
            {step === 1 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div><BusinessPrerequisites /><BusinessSingpassButton onClick={triggerSingpassSync} /></div>
                <div style={{ color: "grey", marginTop: "10px" }}>
                  No Business?{" "}
                  <span
                    onClick={handleSyncWithMyinfo}
                    style={{
                      textDecoration: "underline",
                      color: "var(--br-primary, rgb(14,90,165))",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Sync Singpass
                  </span>{" "}
                  to get your free CBS access code.
                </div>
              </div>
            ) : step === 2 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                }}
              >
                <div
                  className="mui_button step3s"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--br-primary, #1552F0) 0%, var(--br-primary, #2f6bff) 100%)",
                    padding: "12px 24px",
                    borderRadius: "var(--br-card-radius, 14px)",
                    boxShadow: "var(--br-shadow, 0 14px 26px -12px rgba(21,82,240,.75))",
                    fontWeight: 800,
                  }}
                  onClick={() => {
                    trackCbsAnalyticsEvent("cbs_report_cta_clicked", {
                      variant: cbsIntentVariant,
                      action: "get_cbs_report",
                    });
                    setStep(3);
                    window.open(
                      "https://www.creditbureau.com.sg/SmartLend_Financial.html",
                      "_blank"
                    );
                  }}
                >
                  Get CBS Report
                </div>
                <div
                  className="mui_button step2s"
                  style={{
                    color: "var(--br-primary, #1552F0)",
                    background: "var(--br-surface, #FFFFFF)",
                    outline: "1.5px solid #cfdcff",
                    padding: "12px 24px",
                    borderRadius: "var(--br-card-radius, 14px)",
                    fontWeight: 800,
                  }}
                  onClick={() => {
                    trackCbsAnalyticsEvent("cbs_report_already_have_clicked", {
                      variant: cbsIntentVariant,
                      action: "already_have_report",
                    });
                    setStep(3);
                  }}
                >
                  Show Me What I Qualify For
                </div>
              </div>
            ) : step === 3 ? (
              hasPropertyFinancingIntent ? (
                <CbsRefiSavingsSimulator
                  answers={cbsIntentAnswers}
                  onLeave={handlePostCbsQuestionnaireLeave}
                  onStart={handlePostCbsQuestionnaireStart}
                  onSubmit={handlePostCbsLoanSubmit}
                />
              ) : (
                <CbsLoanPreferenceQuestionnaire
                  answers={cbsIntentAnswers}
                  onLeave={handlePostCbsQuestionnaireLeave}
                  onStart={handlePostCbsQuestionnaireStart}
                  onSubmit={handlePostCbsLoanSubmit}
                />
              )
            ) : null}
          </div>
        </div>
      ) : singpassData ? (
        <div
          style={{
            overflow: "hidden",
            borderRadius: "15px",
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            color: "grey",
          }}
        >
          <div style={{ width: window.innerWidth < 1200 ? "100%" : "30%" }}>
            <div
              style={{
                color: "var(--br-text, #142C44CC)",
                fontSize: "20px",
                fontFamily: "Manrope,sans-serif",
                fontWeight: 800,
                marginBottom: "15px",
                width: window.innerWidth < 1200 ? "100%" : "55%",
                textAlign: "left",
              }}
            >
              Financing Options
            </div>
            <div
              style={{
                fontSize: "12px",
                marginTop: "-15px",
                width: window.innerWidth < 1200 ? "100%" : "80%",
              }}
            >
              This fields only appear when candidates meets the requirements to
              be eligible to loan with better rates.
            </div>
            <div
              style={{
                width: window.innerWidth < 1200 ? "100%" : "90%",
                padding: "12px 20px",
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: "var(--br-card-radius, 8px)",
                background: "rgba(256,256,256,0.8)",
                cursor: "pointer",
                marginTop: "20px",
              }}
              className="mui_button"
              onClick={() => {
                history.push("/b-loan-hub");
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <img
                  src={SlLogo}
                  style={{
                    height: "40px",
                    objectFit: "contain",
                    opacity: 0.8,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "23px",
                      //   color: "#256199",
                      color: "rgb(90,90,90)",
                    }}
                  >
                    SmartLend.
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  color: "rgba(0,0,0,0.7)",
                  fontWeight: 700,
                  fontSize: "18px",
                }}
              >
                Peer-to-peer financing
              </div>
              <div
                style={{
                  fontSize: "12px",
                  marginTop: "2px",
                  display: "flex",
                  gap: "5px",
                }}
              >
                <DoneIcon style={{ color: "#91c7a0" }} />
                <span style={{ fontWeight: 600 }}>$200,000</span> maximum loan
                quantum
              </div>
              <div
                style={{
                  fontSize: "12px",
                  display: "flex",
                  gap: "5px",
                }}
              >
                <DoneIcon style={{ color: "#91c7a0" }} />
                Up-to <span style={{ fontWeight: 600 }}>60 months</span> loan
                tenor
              </div>
              <div
                style={{
                  fontSize: "12px",
                  display: "flex",
                  gap: "5px",
                }}
              >
                <DoneIcon style={{ color: "#91c7a0" }} />
                <span style={{ fontWeight: 600 }}>Negotiable</span> interest and
                processing fees
              </div>
            </div>

            {eligibleForFundingSocietyLoan ? (
              <div
                style={{
                  width: window.innerWidth < 1200 ? "100%" : "90%",
                  padding: "12px 20px",
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: "var(--br-card-radius, 8px)",
                  background: "rgba(256,256,256,0.8)",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
                className="mui_button"
                onClick={() => {
                  window.open(
                    "https://fundingsocieties.com/startupfinancing?utm_source=partnership&utm_medium=smarttowkay&utm_campaign=sg_brw_acq_startupfin_partnerships&utm_term=website&utm_content=outreach",
                    "_blank"
                  );
                }}
              >
                <img
                  src={SVGICON}
                  style={{
                    height: "40px",
                    objectFit: "contain",
                    opacity: 0.8,
                  }}
                />
                <div
                  style={{
                    marginTop: "10px",
                    color: "rgba(0,0,0,0.7)",
                    fontWeight: 700,
                    fontSize: "18px",
                  }}
                >
                  Start-up financing
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "2px",
                    display: "flex",
                    gap: "5px",
                  }}
                >
                  <DoneIcon style={{ color: "#91c7a0" }} />
                  <span style={{ fontWeight: 600 }}>$10,000</span> loan quantum
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    display: "flex",
                    gap: "5px",
                  }}
                >
                  <DoneIcon style={{ color: "#91c7a0" }} />
                  <span style={{ fontWeight: 600 }}>5 months</span> loan tenor
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    display: "flex",
                    gap: "5px",
                  }}
                >
                  <DoneIcon style={{ color: "#91c7a0" }} />
                  <span style={{ fontWeight: 600 }}>ZERO</span> interest and
                  fees on timely repayments
                </div>
              </div>
            ) : null}
          </div>
          {window.innerWidth < 1200 ? null : (
            <div style={{ width: "70%" }}>
              <div
                style={{
                  color: "var(--br-text, #142C44CC)",
                  fontSize: "20px",
                  fontFamily: "Manrope,sans-serif",
                  fontWeight: 800,
                  marginBottom: "15px",
                  width: "55%",
                  textAlign: "left",
                }}
              >
                Your financial Summary
              </div>
              <PDFViewer
                style={{
                  width: "100%",
                  height: "80vh",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Document>
                  <Page size="A4">
                    {/**Letterhead section */}
                    <View
                      style={{
                        paddingVertical: 15,
                      }}
                    >
                      <View
                        style={{
                          paddingHorizontal: 40,
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 10,
                          // borderBottom: 1,
                          // borderBottomColor: "grey",
                          paddingBottom: 10,
                        }}
                      >
                        <Image
                          source={logo}
                          style={{ width: 80, objectFit: "contain" }}
                        />
                        <View>
                          <Text
                            style={{
                              fontSize: 18,
                              fontFamily: "Poppins",
                              fontWeight: 700,
                            }}
                          >
                            SmartLend Financial Pte. Ltd.
                          </Text>
                          <View
                            style={{
                              flexDirection: "row",
                              gap: 4,
                              justifyContent: "center",
                              alignItems: "center",
                              marginTop: -3,
                            }}
                          >
                            <Text style={{ fontSize: 7 }}>UEN: 202327568G</Text>
                            <Text style={{ fontSize: 7 }}>
                              Tel: +65 87853373
                            </Text>
                            <Text style={{ fontSize: 7 }}>
                              Email: support@smart-lend.com
                            </Text>
                          </View>
                        </View>
                      </View>
                      <View
                        style={{
                          backgroundColor: "lightgrey",
                          height: 2,
                          width: "100%",
                          marginTop: 20,
                        }}
                      />
                      <View
                        style={{
                          backgroundColor: "#2881c4",
                          height: 4,
                          width: "76%",
                          marginTop: -3,
                          marginLeft: "12%",
                          marginBottom: 15,
                        }}
                      />
                    </View>
                    <View></View>
                  {combinedSingpassData
                    ? combinedSingpassData.map((data, index) => (
                          <View key={index}
                            style={{
                              paddingVertical: 15,
                            }}
                          >
                            {allCBSData?.filter(
                              (dat2) => dat2?.name === getPersonData(data)?.name?.value
                            )?.[0] ? (
                              <View
                                style={{
                                  marginTop: 15,
                                  paddingHorizontal: 40,
                                }}
                              >
                                <View
                                  style={{
                                    backgroundColor: "var(--br-soft, #c7dcec)",
                                    alignItems: "center",
                                    padding: 6,
                                    borderTopLeftRadius: 7,
                                    borderTopRightRadius: 7,
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 10,
                                      fontFamily: "Poppins",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {"Credit Bureau | Personal Commitement"}
                                  </Text>
                                </View>
                                <View
                                  style={{
                                    flexDirection: "row",
                                    padding: 8,
                                    borderColor: "var(--br-soft, #c7dcec)",
                                    borderLeftWidth: 2,
                                    borderRightWidth: 2,
                                  }}
                                >
                                  <View style={{ width: "50%" }}>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Secured Credit Limit:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {allCBSData
                                          ?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]
                                          ?.cbs_data?.SecuredCreditLimit?.toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "SGD",
                                            }
                                          )}
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Credit Score:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {
                                          allCBSData?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]?.cbs_data?.CreditScore
                                        }
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Probability of Default:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {
                                          allCBSData?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]?.cbs_data?.ProbabilityofDefault
                                        }
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Unsecured Credit Utilized:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {allCBSData
                                          ?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]
                                          ?.cbs_data?.UnsecuredCreditUtilized?.toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "SGD",
                                            }
                                          )}
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Unsecured Credit Utilization in
                                        percentage:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {(Number(
                                          allCBSData?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]?.cbs_data
                                            ?.UnsecuredCreditUtilizationinpercentage
                                        ) || 0) + "%"}
                                      </Text>
                                    </View>
                                  </View>
                                  <View style={{ width: "50%" }}>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Unsecured Credit Limit:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {allCBSData
                                          ?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]
                                          ?.cbs_data?.UnsecuredCreditLimit?.toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "SGD",
                                            }
                                          )}
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Risk Grade:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {
                                          allCBSData?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]?.cbs_data?.RiskGrade
                                        }
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Secured Credit Utilized:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {allCBSData
                                          ?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]
                                          ?.cbs_data?.SecuredCreditUtilized?.toLocaleString(
                                            "en-US",
                                            {
                                              style: "currency",
                                              currency: "SGD",
                                            }
                                          )}
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Secured Credit Utilization in
                                        percentage:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {(Number(
                                          allCBSData?.filter(
                                            (dat2) =>
                                              dat2?.name ===
                                              getPersonData(data)?.name?.value
                                          )?.[0]?.cbs_data
                                            ?.SecuredCreditUtilizationinpercentage
                                        ) || 0) + "%"}
                                      </Text>
                                    </View>
                                    <View
                                      style={{
                                        flexDirection: "row",
                                        gap: 10,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: 7,
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Monthly Commitment/Installment Secured:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 7,
                                        }}
                                      >
                                        {(
                                          Number(
                                            allCBSData?.filter(
                                              (dat2) =>
                                                dat2?.name ===
                                                getPersonData(data)?.name?.value
                                            )?.[0]?.cbs_data?.MonthlyCommitment
                                          ) || 0
                                        )?.toLocaleString("en-US", {
                                          style: "currency",
                                          currency: "SGD",
                                        })}
                                      </Text>
                                    </View>
                                  </View>
                                </View>
                                <View
                                  style={{
                                    padding: 8,
                                    borderColor: "var(--br-soft, #c7dcec)",
                                    borderLeftWidth: 2,
                                    borderRightWidth: 2,
                                  }}
                                >
                                  <View
                                    style={{
                                      flexDirection: "row",
                                      width: "100%",
                                      backgroundColor: "var(--br-soft, #c7dcec)",
                                      opacity: 0.7,
                                      padding: 4,
                                      gap: 10,
                                    }}
                                  >
                                    <View
                                      style={{
                                        width: "30%",
                                        fontSize: 7,
                                        fontFamily: "Poppins",
                                      }}
                                    >
                                      <Text>Loan / Product Type</Text>
                                    </View>
                                    <View
                                      style={{
                                        width: "30%",
                                        fontSize: 7,
                                        fontFamily: "Poppins",
                                      }}
                                    >
                                      <Text>Grantor / Bank</Text>
                                    </View>
                                    <View
                                      style={{
                                        width: "20%",
                                        fontSize: 7,
                                        fontFamily: "Poppins",
                                      }}
                                    >
                                      <Text>Outstanding Balance</Text>
                                    </View>
                                    <View
                                      style={{
                                        width: "20%",
                                        fontSize: 7,
                                        fontFamily: "Poppins",
                                      }}
                                    >
                                      <Text>Monthly Installment</Text>
                                    </View>
                                  </View>

                                  {allCBSData
                                    ?.filter(
                                      (dat2) =>
                                        dat2?.name === getPersonData(data)?.name?.value
                                    )?.[0]
                                    ?.cbs_data?.NoteworthyOngoingLoans?.map(
                                      (imp, index10) => (
                                        <View
                                          key={index10}
                                          style={{
                                            flexDirection: "row",
                                            width: "100%",
                                            backgroundColor:
                                              index10 % 2
                                                ? "rgba(199, 220, 236, 0.4)"
                                                : "rgba(199, 220, 236, 0.15)",
                                            padding: 4,
                                            gap: 10,
                                          }}
                                        >
                                          <View
                                            style={{
                                              width: "30%",
                                              fontSize: 7,
                                              fontFamily: "Poppins",
                                            }}
                                          >
                                            <Text>
                                              {imp.includes(
                                                "private residential purchase",
                                                ""
                                              )
                                                ? "Private Residential Purchase"
                                                : imp.includes(
                                                    "motor vehicle loan",
                                                    ""
                                                  )
                                                ? "Motor Vehicle Loan"
                                                : imp.includes("hdb loan", "")
                                                ? "HDB Loan"
                                                : ""}
                                            </Text>
                                          </View>
                                          <View
                                            style={{
                                              width: "30%",
                                              fontSize: 7,
                                              fontFamily: "Poppins",
                                            }}
                                          >
                                            <Text>
                                              {imp
                                                .replace(
                                                  "private residential purchase",
                                                  ""
                                                )
                                                .replace(
                                                  "motor vehicle loan",
                                                  ""
                                                )
                                                .replace("hdb loan", "")
                                                .trim()
                                                .split(" ")[0]
                                                .toUpperCase()}
                                            </Text>
                                          </View>
                                          <View
                                            style={{
                                              width: "20%",
                                              fontSize: 7,
                                              fontFamily: "Poppins",
                                            }}
                                          >
                                            <Text>
                                              {parseFloat(
                                                imp
                                                  .split("   ")
                                                  [
                                                    imp.split("   ").length - 4
                                                  ].replace(/,/g, "")
                                              ).toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "SGD",
                                              })}
                                            </Text>
                                          </View>
                                          <View
                                            style={{
                                              width: "20%",
                                              fontSize: 7,
                                              fontFamily: "Poppins",
                                            }}
                                          >
                                            <Text>
                                              {allCBSData?.filter(
                                                (dat2) =>
                                                  dat2?.name ===
                                                  getPersonData(data)?.name?.value
                                              )?.[0]?.cbs_data
                                                ?.CBSImpLoanSupplementaryInstallment?.[
                                                index10
                                              ]
                                                ? (
                                                    parseFloat(
                                                      allCBSData
                                                        ?.filter(
                                                          (dat2) =>
                                                            dat2?.name ===
                                                            getPersonData(data)?.name
                                                              ?.value
                                                        )?.[0]
                                                        ?.cbs_data?.CBSImpLoanSupplementaryInstallment[
                                                          index10
                                                        ].split("   ")
                                                        [
                                                          allCBSData
                                                            ?.filter(
                                                              (dat2) =>
                                                                dat2?.name ===
                                                                getPersonData(data)
                                                                  ?.name?.value
                                                            )?.[0]
                                                            ?.cbs_data?.CBSImpLoanSupplementaryInstallment[
                                                              index10
                                                            ].split("   ")
                                                            .length - 4
                                                        ]?.replace(/,/g, "")
                                                    ) +
                                                    parseFloat(
                                                      allCBSData
                                                        ?.filter(
                                                          (dat2) =>
                                                            dat2?.name ===
                                                            getPersonData(data)?.name
                                                              ?.value
                                                        )?.[0]
                                                        ?.cbs_data?.CBSImpLoanSupplementaryInstallment[
                                                          index10
                                                        ].split("   ")
                                                        [
                                                          allCBSData
                                                            ?.filter(
                                                              (dat2) =>
                                                                dat2?.name ===
                                                                getPersonData(data)
                                                                  ?.name?.value
                                                            )?.[0]
                                                            ?.cbs_data?.CBSImpLoanSupplementaryInstallment[
                                                              index10
                                                            ].split("   ")
                                                            .length - 3
                                                        ]?.replace(/,/g, "")
                                                    )
                                                  ).toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: "SGD",
                                                  })
                                                : null}
                                            </Text>
                                          </View>
                                        </View>
                                      )
                                    )}
                                </View>
                                <View
                                  style={{
                                    padding: 8,
                                    borderColor: "var(--br-soft, #c7dcec)",
                                    borderLeftWidth: 2,
                                    borderRightWidth: 2,
                                    borderBottomWidth: 2,
                                  }}
                                >
                                  <View
                                    style={{
                                      flexDirection: "row",
                                      gap: 10,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 7,
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                      }}
                                    >
                                      Last 6 Months (Unsecured) Credit
                                      Utilization Graph:
                                    </Text>
                                  </View>
                                  <View
                                    style={{
                                      width: "100%",
                                      fontSize: 7,
                                    }}
                                  >
                                    <View style={{ flexDirection: "row" }}>
                                      <View
                                        style={{
                                          width: "5%",
                                          position: "relative",
                                        }}
                                      >
                                        <View style={{ height: "15px" }}>
                                          <Text
                                            style={{
                                              position: "absolute",
                                              top: 0,
                                              left: 0,
                                            }}
                                          >
                                            {allCBSData
                                              ?.filter(
                                                (dat2) =>
                                                  dat2?.name ===
                                                  getPersonData(data)?.name?.value
                                              )?.[0]
                                              ?.cbs_data?.Last6MonthsGraphChartMaxData?.toLocaleString(
                                                "en-US",
                                                {
                                                  style: "currency",
                                                  currency: "SGD",
                                                }
                                              )}
                                          </Text>
                                        </View>
                                        <View
                                          style={{ height: "220px" }}
                                        ></View>
                                      </View>
                                      <View
                                        style={{
                                          width: "95%",
                                        }}
                                      >
                                        <View
                                          style={{
                                            width: "100%",
                                            flexDirection: "row",
                                            marginTop: "25px",
                                            borderBottom: "1px solid lightgrey",
                                            height: "200px",
                                            alignItems: "flex-end",
                                          }}
                                        >
                                          <View
                                            style={{
                                              height: "200px",
                                              borderLeft: "1px solid lightgrey",
                                            }}
                                          />
                                          {allCBSData
                                            ?.filter(
                                              (dat2) =>
                                                dat2?.name ===
                                                getPersonData(data)?.name?.value
                                            )?.[0]
                                            ?.cbs_data?.Last6MonthsGraphChartData?.map(
                                              (dat30, index30) => (
                                                <View
                                                  key={index30}
                                                  style={{
                                                    marginLeft: "4%",
                                                    marginRight: "4%",
                                                    width: "8%",
                                                    borderTopLeftRadius: "4px",
                                                    borderTopRightRadius: "4px",
                                                    height: `${
                                                      (parseFloat(
                                                        dat30.value.replace(
                                                          /,/g,
                                                          ""
                                                        )
                                                      ) /
                                                        allCBSData?.filter(
                                                          (dat2) =>
                                                            dat2?.name ===
                                                            getPersonData(data)?.name
                                                              ?.value
                                                        )?.[0]?.cbs_data
                                                          ?.Last6MonthsGraphChartMaxData) *
                                                      100
                                                    }%`,
                                                    backgroundColor:
                                                      "rgba(217, 238, 255, 1)",
                                                  }}
                                                ></View>
                                              )
                                            )}
                                        </View>
                                        <View
                                          style={{
                                            marginTop: "8px",
                                            flexDirection: "row",
                                            fontSize: 7,
                                          }}
                                        >
                                          {allCBSData
                                            ?.filter(
                                              (dat2) =>
                                                dat2?.name ===
                                                getPersonData(data)?.name?.value
                                            )?.[0]
                                            ?.cbs_data?.Last6MonthsGraphChartData?.map(
                                              (dat, index) => (
                                                <View
                                                  key={index}
                                                  style={{
                                                    width: "16%",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <Text
                                                    style={{
                                                      fontWeight: 600,
                                                      fontSize: 7,
                                                    }}
                                                  >
                                                    {dat.date
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                      dat.date.slice(1)}
                                                  </Text>
                                                  <Text
                                                    style={{
                                                      fontWeight: 600,
                                                      fontSize: 7,
                                                    }}
                                                  >
                                                    {parseFloat(
                                                      dat.value.replace(
                                                        /,/g,
                                                        ""
                                                      )
                                                    ).toLocaleString("en-US", {
                                                      style: "currency",
                                                      currency: "SGD",
                                                    })}
                                                  </Text>
                                                </View>
                                              )
                                            )}
                                        </View>
                                      </View>
                                    </View>
                                  </View>
                                </View>
                              </View>
                            ) : null}
                          </View>
                      ))
                    : <View><Text>no data</Text></View>}
                  </Page>
                </Document>
              </PDFViewer>
            </div>
          )}
        </div>
      ) : (
        <div></div>
      )}
      <Tour
        accentColor="#7057F6"
        steps={tourSteps}
        style={{ color: "black" }}
        isOpen={shouldShowCbsTour}
        onRequestClose={() => setIsTourOpen(false)}
      />
    </div>
  );
};

export default CbsCampagin;
